import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// v0.297.0 - the item card opens over the page, as in the app.
import { createPortal } from 'react-dom';
// 2026-09-15 — iPhone photos (HEIC) become JPEG before they are read.
import { toUploadableImage } from '../utils/heicPhotos';
import { useParams } from 'react-router';
import { apiGet, fmtDate } from '../api';
import { statusCounts, statusLabel, isOutstanding } from '../utils/defectStatus';
import { theme, card, btnSecondary, input as inputStyle } from '../theme';
// v0.17.1 — Lucide migration.
import { Lock, AlertTriangle, CheckCircle2, MapPin, User, Download, ImagePlus, X, Globe, FileText, RefreshCw, ChevronLeft, ChevronRight, MessageSquare, Flag } from 'lucide-react';
// v0.36.0 — Trade-coloured pins on the public view (was status-coloured).
import { tradeColor, setTradeCatalogue } from '../utils/trades';
// 2026-09-15 — the plan's markup (measured areas, the Legend stamp), read-only.
import MarkupLayer from '../markup/MarkupLayer.jsx';
import { readOnlyMarkup } from '../markup/readOnly.mjs';
// v0.36.1 — Lightbox moved to a shared component so PlanScreen's defect
//           modal can reuse it.
import PhotoLightbox from '../components/PhotoLightbox';
import { pairFirst } from '../utils/photoPairs';
// v0.279.0 — marks burnt into a copy of a photo for the conversation.
import { burnMarks } from '../components/PhotoMarkupStage';
import { usePanZoom } from '../utils/usePanZoom';
import DropPinWordmark from '../components/DropPinWordmark';
import { makeImageVariants, makePhotoBlobs } from '../utils/imageVariants';
// v0.276.0 - live messages (a blob marker, never the database) and the round
// badge on each message.
import { watchLive, presenceBeacon } from '../utils/live';
import { PresenceNote } from '../components/LiveChat';
import {
  CHAT_TABS, chatOf, isOutsider, messageLabel, MessageAvatar, ConversationLabel,
  ChatTabs, ThreadBox, MessageRow, MessageText, MessagePhoto, MessagePhotos, Composer,
  PhotoAttachments, AddPhotosButton, FlagTag, MessageMenuButton,
} from '../components/ConversationParts';
// v0.320.0 - photos sent together read as one message, as in the app.
import { photoRuns } from '../utils/photoRuns';
import { MAX_CHAT_PHOTOS, photosForChat, photosForChatError } from '../utils/chatPhotos';
// v0.284.0 - which item this page is on, for the badges at the top of each
// conversation.
import { pickOnItem } from '../utils/presenceFocus';
// v0.280.0 - when the office's newest message has been on screen long enough
// to count as seen.
import { newestOfficeId, watchSeen } from '../utils/seenOnScreen.mjs';
// v0.286.0 - the page waits behind the opening splash until the report is ready.
import { claimBootSplash, retireBootSplash, setBootSplashText } from '../utils/bootSplash';
// v0.291.0 - the page wears the report's style and accent (what its PDF prints).
import { reportWebLook, lookCss } from '../utils/report-web-look.js';
// v0.292.0 - the sender's PDF, built ONE way for Download and for reading in
// the page (clientReportSettings is applied inside, so the page's look and the
// PDF still take one rule), and the repo's own pdf.js binding to draw it.
import { buildClientReportPdf, reportPdfFingerprint, downloadPdfBytes } from '../utils/clientReportPdf.js';
import { openPdf, warmPdfEngine, clampScale } from '../utils/pdfRaster.js';

// PublicReportScreen - what recipients see when they hit /r/<token>.
//
// IMPORTANT: this screen renders OUTSIDE <AppShell> - no DropPin admin
// chrome, no nav, no version stamp. Just a clean read-only view.
//
// Route: /r/:token (registered in App.jsx outside the AppShell branch)
//
// v0.8.0 - initial static report.
// v0.13.0 - Made INTERACTIVE per Brett's brief: cover page, click a
//           pin to jump to the defect, click a defect to jump back to
//           the plan with the pin highlighted, click a photo to open
//           in a lightbox modal. Each defect card now shows a small
//           plan thumbnail with just its own pin. Still READ-ONLY -
//           no add/edit/rectify actions (those land in v0.14.0+).
// v0.13.1 - DefectCard layout now MIRRORS the PDF: plan thumb LEFT,
//           text MIDDLE, photo grid RIGHT. Was: plan-thumb right,
//           photos inline under meta. Brett: "Shift photo thumbnails
//           off to the side... formating on the report doesnt match
//           what you see on the report when you preview it."
export default function PublicReportScreen() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [errStatus, setErrStatus] = useState(null);
  const [data, setData] = useState(null);

  // v0.13.0 - highlight state shared across plan + list. Stores the
  // defectId that's currently pulsing.
  const [highlightId, setHighlightId] = useState(null);
  const highlightTimer = useRef(null);
  function flash(id, ms = 1800) {
    setHighlightId(id);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightId(null), ms);
  }

  // v0.13.0 / v0.36.0 — Photo lightbox state. Was a single URL;
  // now an object { photos: [{url, ...}], idx: number } so the
  // lightbox can paginate when a defect has multiple photos.
  const [lightboxState, setLightboxState] = useState(null);
  // v0.274.0 — Brett: "the photo viewer should sit over the defect side so
  // comms side stays open." v0.297.0 - the item opens in a card, as in the
  // app: a photo from it opens over the card's item side (the app's rule), so
  // the conversation beside it stays usable. On a phone, or a card with no
  // conversation column, the viewer is the centred one.
  const itemFormRef = useRef(null);
  const overItemForm = useCallback(() => {
    const el = itemFormRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { left: r.left, top: r.top, width: r.width, height: r.height };
  }, []);

  // v0.15.0 - rectify modal state. Set to the defect object when the
  // user clicks "Mark rectified" on a card; the modal reads it to
  // know which defect to act on. v0.16.0 adds a parallel
  // completeDefect for owner sign-off.
  const [rectifyDefect, setRectifyDefect] = useState(null);
  const [completeDefect, setCompleteDefect] = useState(null);
  const [toast, setToast] = useState(null);
  const wide = useWide();
  function flashToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }
  // After a successful action, patch the local defects array so the
  // status pill / pin colour / count cards all update without a full
  // re-fetch. Also tack the new comment + photo on so they appear
  // inline immediately.
  function applyDefectUpdate({ defect, comment, photo }) {
    setData(prev => {
      if (!prev) return prev;
      const nextDefects = prev.defects.map(d => {
        if (d.id !== defect.id) return d;
        const photos = [...(d.photos || [])];
        if (photo) photos.push(photo);
        const comments = [...(d.comments || [])];
        if (comment) comments.push(comment);
        return { ...d, ...defect, photos, comments };
      });
      return { ...prev, defects: nextDefects };
    });
  }
  // v0.264.0 - a reply sent from this page joins its item's thread (and its
  // photo the item's photos, as the sender's own report will show it).
  function applyReply(defectId, comment, photo) {
    setData(prev => {
      if (!prev) return prev;
      const nextDefects = prev.defects.map(d => d.id !== defectId ? d : {
        ...d,
        photos: photo ? [...(d.photos || []), photo] : d.photos,
        comments: comment ? [...(d.comments || []), comment] : d.comments,
      });
      return { ...prev, defects: nextDefects };
    });
  }
  // v0.322.0 - a flag set or cleared from this page: that message's flag
  // ({ at, office, mine } or null) as the server answered it.
  function applyFlag(defectId, commentId, flag) {
    setData(prev => {
      if (!prev) return prev;
      const nextDefects = prev.defects.map(d => d.id !== defectId ? d : {
        ...d,
        comments: (d.comments || []).map(c => (c.id === commentId ? { ...c, flag: flag || null } : c)),
      });
      return { ...prev, defects: nextDefects };
    });
  }
  function applyRectified(res) {
    applyDefectUpdate(res);
    setRectifyDefect(null);
    flashToast('Marked rectified — thanks. The report is updated.');
  }
  function applyCompleted(res) {
    applyDefectUpdate(res);
    setCompleteDefect(null);
    flashToast('Signed off as complete. Thank you.');
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErrStatus(null);
      try {
        const r = await fetch(`/api/public-report?token=${encodeURIComponent(token)}`);
        if (!r.ok) {
          // v0.271.0 - 410 = the job this report is for was closed by the
          // sender, so the link stopped (it comes back if the job is reopened).
          if (!cancelled) setErrStatus(r.status === 404 || r.status === 410 ? r.status : 500);
          return;
        }
        const json = await r.json();
        // 2026-09-15 — the sender's own pin colours. A recipient has no session,
        // so the app-wide catalogue load fails for them and pins took the
        // built-in colours (and, with no trade sent, all came out grey).
        if (!cancelled && Array.isArray(json.pinTypes)) setTradeCatalogue(json.pinTypes);
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) setErrStatus(500);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // v0.286.0 - ONE WAITING SCREEN. The opening splash stays up until the
  // report (or the reason there is none) is on screen; a report that has not
  // been opened for a while can take up to a minute while the database wakes,
  // and the splash says so rather than looking stuck.
  useEffect(() => {
    claimBootSplash();
    const t = setTimeout(() => setBootSplashText('Still opening your report',
      'This can take up to a minute when the report has not been opened for a while.'), 8000);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => { if (!loading) retireBootSplash(); }, [loading]);

  // v0.276.0 - LIVE. Brett, for a Teams meeting: "if we both have it open, a
  // client could attach a photo and send it to me while im waiting, or text
  // instructions while we are talking." public-report hands this page a live
  // pass (null when live is not set up); the poll reads a small blob marker
  // and only an item it says changed is read again (/api/public-thread), so
  // an open page never keeps the database awake. Hidden tab or 15 minutes
  // untouched = no polling (src/utils/live.js). A refused pass ends it and
  // the badge says to reload; there is no session here to mint another.
  const [liveState, setLiveState] = useState(null);
  // v0.278.0 - who from the office has each item open: { defectId: [{ name, typing }] }.
  const [presence, setPresence] = useState({});
  // v0.279.0 - the name typed in a reply box, so the photo viewer's Mark up
  // can tell whether a link with no email has been given one yet.
  const [replyName, setReplyName] = useState(() => { try { return localStorage.getItem(REPLY_NAME_KEY) || ''; } catch { return ''; } });
  // v0.283.0 - a link that reads both chats (our staff): which tab each item
  // is showing, so a marked-up photo goes into the chat on screen.
  const [chatTab, setChatTab] = useState({});
  const livePass = data ? data.live || null : null;
  const defectIdsRef = useRef(new Set());
  defectIdsRef.current = new Set(data && Array.isArray(data.defects) ? data.defects.map(d => d.id) : []);
  useEffect(() => {
    if (!livePass) { setLiveState(null); setPresence({}); return undefined; }
    let gone = false;
    const w = watchLive({
      pass: livePass,
      since: Date.now(),
      onState: s => { if (!gone) setLiveState(s); },
      onPresence: p => { if (!gone) setPresence(p); },
      onChange: ids => {
        for (const id of ids) {
          if (!defectIdsRef.current.has(id)) continue;
          (async () => {
            try {
              const r = await fetch(`/api/public-thread?token=${encodeURIComponent(token)}&defectId=${id}`, { cache: 'no-store' });
              if (!r.ok || gone) return;
              const j = await r.json();
              if (gone || !j || !Array.isArray(j.comments)) return;
              setData(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  defects: prev.defects.map(d => d.id !== id ? d : {
                    ...d,
                    photos: Array.isArray(j.photos) ? j.photos : d.photos,
                    comments: j.comments,
                  }),
                };
              });
            } catch { /* the next change reads it again */ }
          })();
        }
      },
    });
    return () => { gone = true; w.stop(); setPresence({}); };
  }, [livePass, token]);

  // v0.280.0 - Brett: "Yes to the seen marks". Once the office's newest
  // message on an item has been on screen (src/utils/seenOnScreen.mjs) this
  // page says so, once (/api/public-seen). The office then sees "Seen by"
  // under it and Send update leaves it out of this person's next email.
  // seenRef holds what this page has reported, over the item's seenMark
  // from public-report. A network failure or a server fault is let go so
  // a later sight tries again (resolves false); a refusal (the link gone,
  // the job closed) is final.
  const seenRef = useRef(new Map());
  const reportSeen = useCallback((defectId, commentId) => {
    const before = seenRef.current.get(defectId) || 0;
    if (before >= commentId) return Promise.resolve(true);
    seenRef.current.set(defectId, commentId);
    const undo = () => {
      if (seenRef.current.get(defectId) === commentId) seenRef.current.set(defectId, before);
      return false;
    };
    return fetch('/api/public-seen', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, defectId, lastCommentId: commentId }),
      keepalive: true,
    }).then(r => (r.status >= 500 ? undo() : true), undo);
  }, [token]);

  // v0.284.0 - Brett: "I thought we could display the favocon at the top of
  // the box if they are on?" This page tells the live marker which item it is
  // on (src/utils/presenceFocus.js: the reply box with the cursor, else the
  // item most in view once scrolling has stopped for 1.5 s), so the office
  // and the others on this link's chat see this person's badge at the top of
  // that item's conversation. One beacon for the page; it moves item to item
  // and says "left" on the old one first. Blobs only, never the database; a
  // hidden tab or 15 minutes untouched says nothing (src/utils/live.js).
  const presencePass = data ? data.livePresence || null : null;
  const beaconRef = useRef(null);
  const onItemRef = useRef(null);
  const focusIdRef = useRef(null);
  const defectsRef = useRef([]);
  defectsRef.current = data && Array.isArray(data.defects) ? data.defects : [];
  const chatTabRef = useRef(chatTab);
  chatTabRef.current = chatTab;
  const replyNameRef = useRef(replyName);
  replyNameRef.current = replyName;
  const shareRef = useRef(null);
  shareRef.current = data ? data.share || null : null;
  const goToItem = useCallback((id, chat = null) => {
    const b = beaconRef.current;
    if (!b) return;
    const d = id == null ? null : defectsRef.current.find(x => x.id === id);
    if (!d || !d.pt) { onItemRef.current = null; b.setEnabled(false); return; }
    const sh = shareRef.current || {};
    const f = { d: d.id, g: d.pt };
    if (sh.chat === 'all') f.chat = chat || tabFor(chatTabRef.current, d.comments, d.id);
    const who = !sh.recipientEmail ? replyNameRef.current.trim() : '';
    if (who) f.name = who.slice(0, 60);
    onItemRef.current = d.id;
    b.setFields(f);
    b.setEnabled(true);
  }, []);
  useEffect(() => {
    if (!presencePass) return undefined;
    const b = presenceBeacon({ pass: presencePass, enabled: false });
    beaconRef.current = b;
    let raf = 0;
    let settle = null;
    const idOf = (el) => {
      const box = el && el.closest ? el.closest('[data-convo]') : null;
      const n = box ? Number(box.getAttribute('data-convo')) : NaN;
      return Number.isFinite(n) ? n : null;
    };
    function pick() {
      raf = 0;
      const boxes = Array.from(document.querySelectorAll('[data-convo]')).map((el) => {
        const r = el.getBoundingClientRect();
        return { id: Number(el.getAttribute('data-convo')), top: r.top, bottom: r.bottom };
      });
      goToItem(pickOnItem(boxes, window.innerHeight, focusIdRef.current));
    }
    function soon() { if (!raf) raf = requestAnimationFrame(pick); }
    function later() {
      if (settle) clearTimeout(settle);
      settle = setTimeout(() => { settle = null; soon(); }, 1500);
    }
    function onFocusIn(e) {
      const id = idOf(e.target);
      if (id === focusIdRef.current) return;
      focusIdRef.current = id;
      soon();
    }
    function onFocusOut() {
      setTimeout(() => {
        const id = idOf(document.activeElement);
        if (id === focusIdRef.current) return;
        focusIdRef.current = id;
        later();
      }, 0);
    }
    window.addEventListener('scroll', later, { capture: true, passive: true });
    window.addEventListener('resize', later, { passive: true });
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    later();
    return () => {
      window.removeEventListener('scroll', later, { capture: true });
      window.removeEventListener('resize', later);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      if (settle) clearTimeout(settle);
      if (raf) cancelAnimationFrame(raf);
      b.stop();
      beaconRef.current = null;
      onItemRef.current = null;
    };
  }, [presencePass, goToItem]);
  // A tab switch on a both-chats link moves this person to that chat at
  // once; a name typed on a no-address link goes with the beat once it has
  // stopped changing.
  useEffect(() => {
    if (onItemRef.current != null) goToItem(onItemRef.current);
  }, [chatTab, goToItem]);
  useEffect(() => {
    if (onItemRef.current == null) return undefined;
    const t = setTimeout(() => { if (onItemRef.current != null) goToItem(onItemRef.current); }, 1500);
    return () => clearTimeout(t);
  }, [replyName, goToItem]);
  const onTyping = useCallback((id, chat) => {
    focusIdRef.current = id;
    goToItem(id, chat);
    if (beaconRef.current) beaconRef.current.typing();
  }, [goToItem]);

  // v0.291.0 - the report's style and accent, worked out once per load. Above
  // the early returns, so the hooks keep their order.
  const look = useMemo(() => reportWebLook(data && data.pdf), [data]);
  // v0.292.0 - "View as PDF". The PDF build (shared with Download) and which
  // view is on screen (?view=pdf) also live above the early returns.
  const pdf = useReportPdf(data);
  const [view, setView] = useReportView();

  // v0.297.0 - Brett: "i though you had changed the client report so it had
  // the popup chat like the app?" Each item opens in a card over the page, as
  // in the app: the item on the left, its conversation on the right, Previous
  // and Next to step through. A #defect-N link opens that item, as the app's
  // register does.
  const [openId, setOpenId] = useState(null);
  const draftsRef = useRef(new Map());
  const jumpersRef = useRef(new Map());
  const registerJump = useCallback((id, fn) => {
    jumpersRef.current.set(id, fn);
    return () => { if (jumpersRef.current.get(id) === fn) jumpersRef.current.delete(id); };
  }, []);
  const ordered = useMemo(() => {   // the page's order: each drawing's items, then those on no drawing
    if (!data || !Array.isArray(data.defects)) return [];
    const ds = Array.isArray(data.drawings) ? data.drawings : [];
    const out = [];
    for (const dr of ds) for (const d of data.defects) if (d.drawingId === dr.id) out.push(d);
    for (const d of data.defects) if (!d.drawingId) out.push(d);
    return out;
  }, [data]);
  const blockedRef = useRef(false);
  blockedRef.current = !!(lightboxState || rectifyDefect || completeDefect);
  const closeItem = useCallback(() => { setLightboxState(null); setOpenId(null); }, []);
  // A link to one item (#defect-N) opens it once the report is in, and again
  // when the address changes to another; a stale one says why nothing opened.
  const hashOpened = useRef(null);
  const ready = !loading && !!data;
  useEffect(() => {
    if (!ready) return undefined;
    function fromHash() {
      const m = /^#defect-(\d+)$/.exec(window.location.hash || '');
      if (!m || hashOpened.current === m[0]) return;
      hashOpened.current = m[0];
      const id = Number(m[1]);
      if (defectsRef.current.some(d => d.id === id)) setOpenId(id);
      else flashToast('The item from that link is not on this report — it may have been deleted or moved.');
    }
    fromHash();
    window.addEventListener('hashchange', fromHash);
    return () => window.removeEventListener('hashchange', fromHash);
  }, [ready]);
  useEffect(() => { if (view === 'pdf') setOpenId(null); }, [view]);
  useEffect(() => {
    if (openId != null && !ordered.some(d => d.id === openId)) setOpenId(null);
  }, [openId, ordered]);
  // The office sees this person on the item they have open; closing it hands
  // back to the page's own pick (the item most in view).
  const lastOpenRef = useRef(null);
  useEffect(() => {
    if (openId != null) {
      focusIdRef.current = openId;
      goToItem(openId);
    } else if (lastOpenRef.current != null) {
      focusIdRef.current = null;
      goToItem(null);
      const row = document.getElementById(`defect-${lastOpenRef.current}`);
      if (row) {
        // The row of the item last open, which Next may have moved off the
        // one first clicked, is put in view and pulsed so the reader sees
        // where they are.
        row.focus({ preventScroll: true });
        row.scrollIntoView({ block: 'nearest' });
        flash(lastOpenRef.current);
      }
    }
    lastOpenRef.current = openId;
  }, [openId, goToItem]);

  if (loading) {
    return <Wrapper><div style={{ ...card, padding: 40, textAlign: 'center', color: theme.textLight }}>Loading report…</div></Wrapper>;
  }
  if (errStatus === 404) {
    return (
      <Wrapper>
        <div style={{ ...card, padding: 36, textAlign: 'center' }}>
          <Lock size={36} color={theme.textLight} strokeWidth={1.5} style={{ marginBottom: 8 }} />
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>This share link has expired or been revoked</h1>
          <div style={{ fontSize: 13, color: theme.textLight }}>
            {/* v0.287.0 — plain words. The page cannot name the sender here: the
                server answers every dead link the same way on purpose. */}
            If you still need it, ask the person who sent it to you for a new link.
          </div>
        </div>
      </Wrapper>
    );
  }
  if (errStatus === 410) {
    return (
      <Wrapper>
        <div style={{ ...card, padding: 36, textAlign: 'center' }}>
          <CheckCircle2 size={36} color={theme.textLight} strokeWidth={1.5} style={{ marginBottom: 8 }} />
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>This job has been closed</h1>
          <div style={{ fontSize: 13, color: theme.textLight }}>
            The link to this report stopped working when the job was closed. If you still need it, contact the sender.
          </div>
        </div>
      </Wrapper>
    );
  }
  if (errStatus === 500 || !data) {
    return (
      <Wrapper>
        <div style={{ ...card, padding: 36, textAlign: 'center' }}>
          <AlertTriangle size={36} color={theme.warn} strokeWidth={1.5} style={{ marginBottom: 8 }} />
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>Something went wrong loading this report</h1>
          <div style={{ fontSize: 13, color: theme.textLight }}>Please try again in a moment.</div>
        </div>
      </Wrapper>
    );
  }

  const { share, project, drawings, defects, projectContacts = [], reportTeamMembers = [] } = data;
  // v0.279.0 - Brett: "Building that on clients screen too" (the office's
  // mark-up: "Just 3 tools, text, arrow and pen"). A link that can reply marks
  // up a photo in the viewer and posts the copy as its reply in that item's
  // conversation; the item's photo is never changed and the copy shows in its
  // message only. A link with no email gives its name in the reply box first.
  const markItemId = lightboxState ? (lightboxState.defectId ?? lightboxState.markId ?? null) : null;
  const clientMarkup = ['reply', 'rectify', 'owner'].includes(share.permission) && markItemId != null ? {
    whyNot: () => {
      if (!share.recipientEmail && !replyName.trim()) return 'Add your name in the reply box first so they know who replied.';
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'Posting a marked-up photo needs a connection.';
      return null;
    },
    onPost: async (p, marks) => {
      const who = replyName.trim();
      if (!share.recipientEmail && !who) return { ok: false, error: 'Add your name in the reply box first so they know who replied.' };
      let burnt;
      try {
        burnt = await burnMarks(p.url || p.displayUrl || p.thumbnailUrl, marks);
      } catch (e) {
        if (!p.thumbnailUrl || p.thumbnailUrl === (p.url || p.displayUrl)) throw e;
        burnt = await burnMarks(p.thumbnailUrl, marks); // the small copy, if the full file will not read
      }
      const v = await makePhotoBlobs(new File([burnt.blob], 'Marked up.jpg', { type: 'image/jpeg' }));
      const [photoUrl, photoDisplayUrl, photoThumbnailUrl] = await Promise.all(
        [v.original, v.display, v.thumbnail].map(b => (b ? readAsDataUrl(b) : null)),
      );
      let r;
      try {
        r = await fetch('/api/public-reply', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token, defectId: markItemId, body: '', authorName: who || null, photoUrl, photoThumbnailUrl, photoDisplayUrl,
            ...(share.chat === 'all' ? { chat: tabFor(chatTab, (defects.find(x => x.id === markItemId) || {}).comments, markItemId) } : {}),
          }),
        });
      } catch (_) {
        return { ok: false, error: 'No connection — the marked-up photo was not posted. Try again when you’re back online.' };
      }
      let j = null;
      try { j = await r.json(); } catch { j = null; }
      if (!r.ok) return { ok: false, error: (j && j.error) || `The marked-up photo was not sent (${r.status}).` };
      if (who) { try { localStorage.setItem(REPLY_NAME_KEY, who); } catch { /* private window */ } }
      applyReply(markItemId, j && j.comment, j && j.photo);
      return { ok: true, held: false };
    },
  } : null;
  // v0.312.0 — defectStatus's counts: On hold apart, Cancelled out of
  // the total and the % done (legacy in_progress = Open, closed = Complete).
  const counts = statusCounts(defects);

  return (
    <Wrapper look={look} view={view} onView={setView}>
    <LookCtx.Provider value={look}>
      {/* v0.292.0 - the web page stays mounted behind the PDF view, so its
          conversations, typed words and open photo keep their state; `hidden`
          takes it off screen and out of the tab order. v0.297.0 - `inert`
          while an item is open, so a key or a screen reader stays in the card. */}
      <div data-report-web hidden={view === 'pdf'} inert={openId != null ? '' : undefined}>
      {/* v0.291.0 - Brett: "Why does the client report not look the same as
          mine?" The cover is the PDF's cover in the report's own style and
          accent (ReportCover); the four summary cards it replaced are its
          KPI row. */}
      <ReportCover look={look} project={project} share={share} counts={counts} pdf={pdf} />

      {/* v0.36.2 — Large-report informational banner. Triggers when the
          report is big enough that first paint feels slow (defects > 25
          OR total inline photos > 30). Tells the recipient the report is
          intentionally lazy-loading so they don't think the page is hung
          while images fill in. */}
      {(() => {
        const totalPhotos = defects.reduce(
          (sum, d) => sum + (Array.isArray(d.photos) ? d.photos.length : 0),
          0
        );
        const isLarge = defects.length > 25 || totalPhotos > 30;
        if (!isLarge) return null;
        return (
          <div
            style={{
              background: theme.bg,
              border: '1px solid ' + theme.border,
              borderLeft: '4px solid var(--report-mark)',
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 14,
              fontSize: 13,
              color: theme.text,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
            role="status"
          >
            <span style={{ fontSize: 18, lineHeight: 1 }} aria-hidden>⏳</span>
            <span>
              <strong>Large report — {defects.length} defects, {totalPhotos} photos.</strong>{' '}
              Photos load as you scroll, so the page may feel "live" for
              a few seconds on first open.
            </span>
          </div>
        );
      })()}

      {/* v0.40.0 — Distribution. The per-report roster (ReportTeamMembers)
          who this share was sent to, grouped by Role. Hidden when
          empty (older shares pre-v0.40.0 won't carry the list). */}
      {Array.isArray(reportTeamMembers) && reportTeamMembers.length > 0 && (
        <div style={{ ...card, padding: 14, marginBottom: 14 }}>
          <div style={{
            fontSize: 11, fontWeight: 800, color: theme.textLight,
            textTransform: 'uppercase', letterSpacing: '0.05em',
            marginBottom: 8,
          }}>Distribution</div>
          {(() => {
            const groups = new Map();
            for (const m of reportTeamMembers) {
              const k = m.role || '_';
              if (!groups.has(k)) groups.set(k, []);
              groups.get(k).push(m);
            }
            const ordered = Array.from(groups.entries()).sort((a, b) => {
              if (a[0] === '_') return 1;
              if (b[0] === '_') return -1;
              return a[0].localeCompare(b[0]);
            });
            return ordered.map(([role, items]) => (
              <div key={role} style={{ marginBottom: 8 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: theme.text,
                  marginBottom: 4, textTransform: 'capitalize',
                }}>
                  {role === '_' ? 'Other' : role}
                  <span style={{ color: theme.textLight, fontWeight: 500 }}> · {items.length}</span>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                  gap: 6,
                }}>
                  {items.map(m => (
                    <div key={m.id} style={{
                      padding: '6px 8px',
                      border: `1px solid ${theme.border}`,
                      borderRadius: 6,
                      fontSize: 12,
                    }}>
                      <div style={{ fontWeight: 600, color: theme.text }}>{m.name}</div>
                      {m.email && <div style={{ color: theme.textLight, fontSize: 11 }}>{m.email}</div>}
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* v0.39.1 — Project Team. Lists contacts picked into this
          project from the tenant's master Contacts list. Grouped by
          Trade so the recipient can scan 'who are the plumbers /
          who are the sparkies'. Hidden when empty (older projects
          or shares created before Contacts existed). */}
      {Array.isArray(projectContacts) && projectContacts.length > 0 && (
        <div style={{ ...card, padding: 14, marginBottom: 14 }}>
          <div style={{
            fontSize: 11, fontWeight: 800, color: theme.textLight,
            textTransform: 'uppercase', letterSpacing: '0.05em',
            marginBottom: 8,
          }}>Project team</div>
          {(() => {
            const groups = new Map();
            for (const c of projectContacts) {
              const k = c.trade || '_';
              if (!groups.has(k)) groups.set(k, []);
              groups.get(k).push(c);
            }
            const ordered = Array.from(groups.entries()).sort((a, b) => {
              if (a[0] === '_') return 1;
              if (b[0] === '_') return -1;
              return a[0].localeCompare(b[0]);
            });
            return ordered.map(([trade, items]) => (
              <div key={trade} style={{ marginBottom: 8 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: 11, fontWeight: 700, color: theme.text,
                  textTransform: 'capitalize', marginBottom: 4,
                }}>
                  {trade !== '_' && (
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: tradeColor(trade), display: 'inline-block',
                    }} />
                  )}
                  <span>{trade === '_' ? 'Other' : trade}</span>
                  <span style={{ color: theme.textLight, fontWeight: 500 }}>· {items.length}</span>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                  gap: 6,
                }}>
                  {items.map(c => (
                    <div key={c.id} style={{
                      padding: '6px 8px',
                      border: `1px solid ${theme.border}`,
                      borderRadius: 6,
                      fontSize: 12,
                    }}>
                      <div style={{ fontWeight: 600, color: theme.text }}>{c.name}</div>
                      {c.company && <div style={{ color: theme.textLight, fontSize: 11 }}>{c.company}</div>}
                      {c.email && <div style={{ color: theme.textLight, fontSize: 11 }}>{c.email}</div>}
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* v0.15.0 - share permission gates the rectify action.
          v0.16.0 - 'owner' tier also unlocks the Complete sign-off. */}
      {(() => {
        const canRectify = share.permission === 'rectify' || share.permission === 'owner';
        const canComplete = share.permission === 'owner';
        // v0.264.0 - a 'reply' link (and rectify / owner) answers under each item.
        const replyCtx = {
          canReply: ['reply', 'rectify', 'owner'].includes(share.permission),
          shareId: share.id,
          token,
          needName: !share.recipientEmail,
          // v0.281.0 - a link sent to a person is that person: the reply box
          // says whose name it goes under and never asks for one.
          speakerName: share.recipientEmail ? (share.recipientName || share.recipientEmail) : null,
          onReplied: applyReply,
          onFlagged: applyFlag,
          onName: setReplyName,
          // v0.283.0 - a link reading both chats shows them as two tabs.
          bothChats: share.chat === 'all',
          tabs: chatTab,
          setTab: (id, k) => setChatTab(t => ({ ...t, [id]: k })),
          // v0.276.0 - the badge beside each conversation, and the sender's
          // logo on their messages (a person's carry their initials).
          live: liveState,
          presence,
          // v0.284.0 - a key in a reply box puts this person on that item, typing.
          onTyping,
          // v0.280.0 - what this page has reported seen, and the report.
          seen: { get: id => seenRef.current.get(id) || 0, report: reportSeen },
          brand: {
            logoUrl: (data.pdf && data.pdf.tenant && data.pdf.tenant.brandLogoUrl) || null,
            name: (data.pdf && data.pdf.tenant && data.pdf.tenant.brandName) || null,
          },
          // v0.297.0 - words typed to an item wait here when its card is
          // closed or stepped past, and the name typed once is used again.
          drafts: draftsRef.current,
          replyName,
        };
        return (
          <WideCtx.Provider value={wide}>
          <ReplyCtx.Provider value={replyCtx}>
            {/* v0.45.5 — Multi-pin per-drawing rollup removed. DropPin
                defects are 1:1 with drawings; the multi-pin / RFI
                workflow lives on InSpec's RFI register. Each drawing
                section just filters defects by drawingId. */}
            {drawings && drawings.length > 0 && drawings.map((drawing) => {
              const defectsHere = defects.filter(d => d.drawingId === drawing.id);
              return (
                <DrawingSection
                  key={drawing.id}
                  drawing={drawing}
                  defects={defectsHere}
                  highlightId={highlightId}
                  onPinClick={(d) => setOpenId(d.id)}
                  onOpen={setOpenId}
                  registerJump={registerJump}
                />
              );
            })}

            {/* Unpinned defects */}
            {(() => {
              const unpinned = defects.filter(d => !d.drawingId);
              if (!unpinned.length) return null;
              return (
                <div style={{ ...card, marginBottom: 14 }}>
                  {/* v0.287.0 — a report with no plans has nothing to be
                      unpinned from: its list is just the items. */}
                  <SectionHeading margin="0 0 12px">
                    {drawings && drawings.length > 0 ? 'Unpinned defects' : 'Items'}
                  </SectionHeading>
                  <DefectList
                    defects={unpinned}
                    drawingsById={new Map()}
                    highlightId={highlightId}
                    onOpen={setOpenId}
                  />
                </div>
              );
            })()}
            {openId != null && (
              <ItemPopup
                d={ordered.find(x => x.id === openId)}
                ordered={ordered}
                onStep={setOpenId}
                onClose={closeItem}
                blockedRef={blockedRef}
                itemFormRef={itemFormRef}
                hasDrawings={!!(drawings && drawings.length > 0)}
                drawingsById={new Map((drawings || []).map(dr => [dr.id, dr]))}
                onJump={(d) => {
                  closeItem();
                  setTimeout(() => { const f = jumpersRef.current.get(d.drawingId); if (f) f(d); }, 0);
                }}
                onPhotoClick={(photos, idx, defectId) => setLightboxState({ photos, idx, defectId })}
                canRectify={canRectify}
                onRectifyClick={setRectifyDefect}
                canComplete={canComplete}
                onCompleteClick={setCompleteDefect}
              />
            )}
          </ReplyCtx.Provider>
          </WideCtx.Provider>
        );
      })()}

      {defects.length === 0 && (
        <div style={{ ...card, padding: 30, textAlign: 'center', color: theme.textLight }}>
          No defects recorded on this project yet.
        </div>
      )}

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '24px 12px 12px', fontSize: 11, color: theme.textLight }}>
        Powered by <span style={{ color: theme.text, fontWeight: 700 }}><DropPinWordmark /></span>
      </div>
      </div>

      {/* v0.292.0 - the sender's PDF, read in the page */}
      {view === 'pdf' && <ReportPdfView pdf={pdf} />}

      {/* v0.13.0 - lightbox (component extracted to ../components/PhotoLightbox in v0.36.1) */}
      {lightboxState && (
        <PhotoLightbox
          contained
          photos={lightboxState.photos}
          initialIdx={lightboxState.idx || 0}
          onClose={() => setLightboxState(null)}
          area={overItemForm}
          markup={clientMarkup}
        />
      )}

      {/* v0.15.0 - rectify modal */}
      {rectifyDefect && (
        <RectifyModal
          defect={rectifyDefect}
          token={token}
          shareRecipient={share.recipientEmail}
          onClose={() => setRectifyDefect(null)}
          onSuccess={applyRectified}
          onError={(msg) => flashToast('Could not mark rectified: ' + msg)}
        />
      )}

      {/* v0.16.0 - owner sign-off modal */}
      {completeDefect && (
        <CompleteModal
          defect={completeDefect}
          token={token}
          shareRecipient={share.recipientEmail}
          onClose={() => setCompleteDefect(null)}
          onSuccess={applyCompleted}
          onError={(msg) => flashToast('Could not sign off: ' + msg)}
        />
      )}

      {/* v0.15.0 - success toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(15,23,42,0.95)', color: '#e2e8f0',
          padding: '10px 18px', borderRadius: 999, fontSize: 13,
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)', zIndex: 1900,
        }}>{toast}</div>
      )}

      {/* v0.13.0 - pulse keyframes for the highlight ring; v0.297.0 - the
          item rows that open the card */}
      <style>{'@keyframes dp-pulse { 0%{box-shadow:0 0 0 0 color-mix(in srgb, var(--report-mark) 40%, transparent)} 100%{box-shadow:0 0 0 18px transparent} } .dp-flash{animation:dp-pulse 1.8s ease-out} .dp-row:hover{border-color:var(--report-mark)} .dp-row:focus-visible{outline:2px solid var(--report-mark);outline-offset:2px}'}</style>
    </LookCtx.Provider>
    </Wrapper>
  );
}

// ----------------------------------------------------------------------
// v0.273.0 — Brett: "yes build the client split too". On a screen 1000 px or
// wider each item shows its conversation in a column of its own on the right,
// as the office's item card does (v0.272.0); narrower (a phone), the
// conversation sits under the item. v0.297.0 - that split is now the open
// item's card (ItemPopup), and the page keeps its reading width.
const WIDE_QUERY = '(min-width: 1000px)';
const WideCtx = React.createContext(false);
function useWide() {
  const [wide, setWide] = useState(() => {
    try { return window.matchMedia(WIDE_QUERY).matches; } catch { return false; }
  });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(WIDE_QUERY); } catch { return undefined; }
    const on = (e) => setWide(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);
  return wide;
}

// ----------------------------------------------------------------------
// v0.291.0 — Brett: "Why does the client report not look the same as mine?"
// The page wears the report's look (utils/report-web-look.js): the cover is the
// PDF's cover in the report's style and accent, the section headings carry its
// heading, and the brand shades re-point at the accent while the page is open.
// The loading and dead-link screens have no report yet: they keep the suite's.
const DEFAULT_LOOK = reportWebLook(undefined);
const LookCtx = React.createContext(DEFAULT_LOOK);
// On a phone the cover packs tighter (numbers three across, details two across,
// slimmer edges) so the first item still shows on the first screen.
const COVER_CSS = '.dp-cover-title{font-size:var(--report-title-px)}'
  + '.dp-cover{--cover-py:22px;--cover-px:24px;padding:var(--cover-py) calc(var(--cover-px) + var(--cover-strip)) var(--cover-py) var(--cover-px)}'
  + '.dp-cover-kpis{display:grid;grid-template-columns:repeat(auto-fit,minmax(110px,1fr));gap:8px;margin-top:16px}'
  + '.dp-cover-kpis[data-cover-kpis="cells"]>div{padding:10px 12px}'
  + '.dp-cover-num{font-size:22px}'
  + '.dp-cover-meta{grid-template-columns:repeat(auto-fill,minmax(200px,1fr))}'
  + '@media (max-width:480px){.dp-cover-title{font-size:var(--report-title-px-sm)}'
  + '.dp-cover{--cover-py:18px;--cover-px:16px}'
  + '.dp-cover-kpis{grid-template-columns:repeat(3,minmax(0,1fr));gap:6px;margin-top:14px}'
  + '.dp-cover-kpis[data-cover-kpis="cells"]>div{padding:8px 8px}'
  + '.dp-cover-num{font-size:20px}'
  + '.dp-cover-meta{grid-template-columns:repeat(2,minmax(0,1fr))}}';

// v0.292.0 — Web page | PDF in the page header. Two toggle buttons in one
// pill (the header's own idiom: a translucent plate on the dark chrome, which
// no report style or accent re-points), the one on screen pressed. Native
// buttons, so Tab reaches them and Enter / Space switch; aria-pressed says
// which is on. Only drawn when the page has a report (onView given).
const VIEWS = [['web', 'Web page', Globe], ['pdf', 'PDF', FileText]];
function ViewSwitch({ view, onView }) {
  return (
    <div role="group" aria-label="Show the report as" data-view-switch style={{
      marginLeft: 'auto', flex: 'none',
      display: 'inline-flex', alignItems: 'center', gap: 2, padding: 2, borderRadius: 999,
      background: 'color-mix(in srgb, var(--on-chrome) 10%, transparent)',
      border: '1px solid color-mix(in srgb, var(--on-chrome) 18%, transparent)',
    }}>
      {VIEWS.map(([key, label, Icon]) => {
        const on = view === key;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={on}
            data-view={key}
            onClick={() => { if (!on) onView(key); }}
            // the PDF is drawn by pdf.js, loaded on demand: a hand hovering
            // the PDF button is the earliest honest moment to fetch it
            onPointerEnter={key === 'pdf' ? warmPdfEngine : undefined}
            onFocus={key === 'pdf' ? warmPdfEngine : undefined}
            style={{
              height: 26, padding: '0 10px', borderRadius: 999, border: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontFamily: 'inherit', fontSize: 12, fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap',
              background: on ? 'var(--on-chrome)' : 'transparent',
              color: on ? 'var(--chrome-900)' : 'var(--on-chrome)',
              cursor: on ? 'default' : 'pointer',
              transition: 'background var(--dur-fast) var(--easing), color var(--dur-fast) var(--easing)',
            }}
          >
            <Icon size={13} strokeWidth={2.25} aria-hidden />
            {label}
          </button>
        );
      })}
    </div>
  );
}

function Wrapper({ children, look = null, view = 'web', onView = null }) {
  const L = look || DEFAULT_LOOK;
  return (
    <div style={{ minHeight: '100vh', background: theme.bg, color: theme.text }}>
      {look && <style>{lookCss(look) + COVER_CSS}</style>}
      <header style={{
        height: 48,
        background: 'var(--chrome-grad)',
        color: 'var(--on-chrome)',
        display: 'flex', alignItems: 'center', gap: 12, padding: '0 18px',
      }}>
        {/* A Pro report's page is the company's: its logo (on a plate, so a
            dark logo still reads on the dark bar) or its name. A Free one is
            DropPin's, in DropPin's own orange whatever the report's accent. */}
        {L.tier === 'pro' && L.logoUrl ? (
          <div data-header-brand="logo" style={{
            display: 'flex', alignItems: 'center',
            padding: '3px 6px', borderRadius: 6, background: 'var(--on-chrome)',
          }}>
            <img src={L.logoUrl} alt={L.brandName} style={{
              display: 'block', maxHeight: 22, maxWidth: 120, width: 'auto', height: 'auto', objectFit: 'contain',
            }} />
          </div>
        ) : L.tier === 'pro' ? (
          <div data-header-brand="name" style={{ fontSize: 14, fontWeight: 700 }}>{L.brandName}</div>
        ) : (
          <div data-header-brand="droppin" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 26, height: 26, borderRadius: 6,
              background: 'var(--droppin-mark)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--on-chrome)',
            }}><MapPin size={16} strokeWidth={2.5} /></div>
            <div style={{ fontSize: 14, fontWeight: 700 }}><DropPinWordmark /></div>
          </div>
        )}
        {onView && <ViewSwitch view={view} onView={onView} />}
      </header>
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '18px 14px 40px' }}>
        {children}
      </div>
    </div>
  );
}

// The PDF's cover, on the web: the report's label, its title with the project,
// the chip, the five numbers and who it was shared with, in the report's style.
function ReportCover({ look, project, share, counts, pdf }) {
  const C = look.cover;
  const centred = C.titleAlign === 'center';
  const pct = counts.pct;
  const kpis = [
    { key: 'total', label: 'Total', value: counts.total },
    { key: 'open', label: 'Open', value: counts.open },
    ...(counts.on_hold ? [{ key: 'hold', label: 'On hold', value: counts.on_hold }] : []),
    { key: 'rect', label: 'Rectified', value: counts.rectified },
    { key: 'comp', label: 'Complete', value: counts.complete },
    { key: 'pct', label: '% Done', value: `${pct}%` },
  ];
  const chip = [project.id != null ? `#${project.id}` : null, 'Project', fmtDate(new Date())].filter(Boolean);
  const meta = [
    ['customer', 'Customer', project.customerName],
    ['location', 'Location', project.address],
    ['prepared', 'Prepared by', look.preparedBy],
    ['shared', 'Shared with', share.recipientName || share.recipientEmail || 'you'],
    ['expires', 'Link expires', share.expiresAt ? fmtDate(share.expiresAt) : null],
    ['opened', 'Project opened', project.createdAt ? fmtDate(project.createdAt) : null],
  ].filter(([, , v]) => v);
  const rule = (extra) => <div aria-hidden style={{ borderTop: '3px double var(--report-paper-line)', ...extra }} />;
  const chipLook = {
    ghost: { background: 'var(--report-chip-bg)', color: 'var(--report-chip-text)' },
    solid: { background: 'var(--report-chip-bg)', color: 'var(--report-chip-text)' },
    outline: { border: '1px solid var(--report-paper-line)', color: 'var(--report-paper-text)' },
    none: { color: 'var(--report-paper-muted)', padding: 0 },
  }[C.chipStyle] || {};
  const kpiCell = {
    cells: { background: 'var(--report-kpi-cell)', border: '1px solid var(--report-kpi-cell-line)', borderRadius: 8 },
    bars: { borderLeft: '3px solid var(--report-accent)', paddingLeft: 10 },
  }[C.kpiStyle] || {};

  // Padding is COVER_CSS's (it steps down on a phone); the strip adds its room on the right.
  return (
    <section className="dp-cover" data-report-cover={look.styleKey} data-cover-key={look.coverKey} style={{
      position: 'relative', overflow: 'hidden',
      background: 'var(--report-paper)', color: 'var(--report-paper-text)',
      border: '1px solid var(--report-paper-border)', borderRadius: C.radius,
      '--cover-strip': C.strip ? '12px' : '0px', marginBottom: 14,
    }}>
      {C.strip && (
        <div aria-hidden data-cover-strip style={{
          position: 'absolute', top: 0, right: 0, bottom: 0, width: 6, background: 'var(--report-accent)',
        }} />
      )}
      {C.band && (
        <div data-cover-band style={{
          margin: 'calc(-1 * var(--cover-py)) calc(-1 * (var(--cover-px) + var(--cover-strip))) 18px calc(-1 * var(--cover-px))',
          padding: '14px var(--cover-px)',
          background: 'var(--report-accent)', color: 'var(--report-on-accent)',
          fontSize: 20, fontWeight: 800, lineHeight: 1.2,
        }}>
          {look.tier === 'pro' ? look.brandName : <DropPinWordmark dotColor="var(--report-on-accent)" />}
        </div>
      )}

      {C.formalRules && rule({ marginBottom: 12 })}
      <div style={{ textAlign: centred ? 'center' : 'left' }}>
        <span data-cover-label style={{
          display: 'inline-block', fontSize: 11, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: 'var(--ls-caps)',
          color: C.labelPill ? 'var(--report-on-fill)' : 'var(--report-cover-ink)',
          ...(C.labelPill ? { background: 'var(--report-fill)', borderRadius: 'var(--radius-full)', padding: '3px 10px' } : null),
        }}>{look.docLabel}</span>
      </div>
      {C.ruleH > 0 && (
        <div aria-hidden data-cover-rule style={{
          height: Math.max(2, Math.round(C.ruleH * 2)), background: 'var(--report-cover-accent)',
          width: C.ruleFull ? '100%' : 40, margin: centred ? '10px auto 0' : '10px 0 0',
        }} />
      )}
      <h1 className="dp-cover-title" style={{
        fontWeight: C.titleWeight, fontFamily: C.font, textAlign: C.titleAlign,
        lineHeight: 1.15, margin: '10px 0 6px', overflowWrap: 'anywhere', color: 'inherit',
      }}>
        {look.title} — {project.name || 'Untitled project'}
      </h1>
      {C.formalRules && rule({ margin: '8px 0 12px' })}
      <div style={{ textAlign: centred ? 'center' : 'left' }}>
        <span data-cover-chip={C.chipStyle} style={{
          display: 'inline-flex', alignItems: 'center', fontSize: 12, fontWeight: 700,
          borderRadius: 'var(--radius-full)', padding: '3px 10px', ...chipLook,
        }}>
          {chip.map((part, i) => (
            <React.Fragment key={part}>
              {i > 0 && <span aria-hidden style={{ margin: '0 8px' }}>•</span>}
              {part}
            </React.Fragment>
          ))}
        </span>
      </div>

      <div className="dp-cover-kpis" data-cover-kpis={C.kpiStyle}>
        {kpis.map(k => (
          <div key={k.key} data-kpi={k.key} style={kpiCell}>
            <div className="dp-cover-num" style={{ fontWeight: 700, fontFamily: C.font, lineHeight: 1.2, color: `var(--report-kpi-${k.key})` }}>{k.value}</div>
            <div style={{
              fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 'var(--ls-caps)',
              color: 'var(--report-kpi-label)', marginTop: 2,
            }}>{k.label}</div>
          </div>
        ))}
      </div>

      {meta.length > 0 && (
        <div className="dp-cover-meta" style={{
          display: 'grid', gap: '10px 18px',
          marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--report-paper-line)',
        }}>
          {meta.map(([key, label, value]) => (
            <div key={key} data-meta={key}>
              <div style={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 'var(--ls-caps)',
                color: 'var(--report-paper-muted)',
              }}>{label}</div>
              <div style={{ fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <DownloadReportPdf pdf={pdf} onPaper />
    </section>
  );
}

// A section heading in the report's heading style: a rule, a bar, a short
// underline, a double rule or nothing, in its font, capitals where it prints them.
function SectionHeading({ children, margin = '0 0 12px' }) {
  const H = React.useContext(LookCtx).heading;
  const deco = {
    rule: { borderBottom: '1px solid var(--border)', paddingBottom: 6 },
    bar: { borderLeft: '4px solid var(--report-mark)', paddingLeft: 10 },
    double: { borderBottom: '3px double var(--text-700)', paddingBottom: 6 },
  }[H.deco] || {};
  return (
    <h2 data-heading={H.deco} style={{
      fontSize: H.px, fontFamily: H.font, fontWeight: H.font === 'var(--font-serif)' ? 700 : 800,
      color: theme.text, margin, lineHeight: 1.3,
      ...(H.caps ? { textTransform: 'uppercase', letterSpacing: 'var(--ls-caps)' } : null),
      ...deco,
    }}>
      {children}
      {H.deco === 'underline' && (
        <span aria-hidden style={{ display: 'block', width: 36, height: 3, marginTop: 6, background: 'var(--report-mark)', borderRadius: 2 }} />
      )}
    </h2>
  );
}

// ----------------------------------------------------------------------
// v0.13.0 - DrawingSection with INTERACTIVE pins. v0.297.0 - a pin, like
// its row in the list, opens the item's card (ItemPopup); the card's plan
// thumbnail comes back here through registerJump to zoom in on the pin.
function DrawingSection({ drawing, defects, highlightId, onPinClick, onOpen, registerJump }) {
  const src = drawing.rasterUrl || drawing.sourceUrl;
  const drawingsById = new Map([[drawing.id, drawing]]);

  // v0.73.0 — the main plan is a pan/zoom viewer (matches the app):
  //   • jumping from a defect's mudmap scrolls the plan into view, zooms in on
  //     that pin, and turns the pin ORANGE;
  //   • drag to pan (mouse or touch); mouse-wheel zooms toward the cursor;
  //   • "Reset" restores the fit-to-width view.
  // 2026-09-14 — the pan/zoom surface is shared with the photo viewer
  // (usePanZoom): wheel zooms toward the cursor, drag pans, and the pointer is
  // claimed only once a drag begins so a pin's click stays a click.
  const boxRef = useRef(null);
  const { view, isZoomed, dragging, reset: resetZoom, centreOn, transform, handlers, surfaceStyle } = usePanZoom(boxRef);
  const [selectedPinId, setSelectedPinId] = useState(null);

  // 2026-09-15 — Brett: "The report doesnt show the correct colour pins, the
  // legend, or the markup area." The plan's markup is drawn read-only over the
  // plan. Markup is stored against the image's natural pixels and this view is
  // fitted to the box, so the layer sits INSIDE the zoom wrapper (it pans and
  // animates with the plan) scaled by box width over natural width, and its
  // labels counter-scale by the zoom on top (outerScale).
  // v0.304.0 — Brett: "hide the m2 on the client report". The marks stay; their
  // m² / length chips and the Legend's totals do not.
  const markupDoc = useMemo(() => (drawing.markup ? readOnlyMarkup(drawing.markup, { figures: false }) : null), [drawing.markup]);
  const [natural, setNatural] = useState(() => (
    drawing.width > 0 && drawing.height > 0 ? { w: drawing.width, h: drawing.height } : null
  ));
  const [boxW, setBoxW] = useState(0);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    setBoxW(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setBoxW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const resetView = () => { resetZoom(); setSelectedPinId(null); };

  const handleJumpToPlan = (d) => {
    const box = boxRef.current;
    if (box && d && typeof d.markX === 'number' && typeof d.markY === 'number') {
      centreOn(2.6, d.markX, d.markY);
      setSelectedPinId(d.id);
      box.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  const jumpRef = useRef(handleJumpToPlan);
  jumpRef.current = handleJumpToPlan;
  useEffect(() => (registerJump ? registerJump(drawing.id, (d) => jumpRef.current(d)) : undefined), [registerJump, drawing.id]);

  return (
    <div id={`drawing-${drawing.id}`} style={{ ...card, marginBottom: 14 }}>
      <SectionHeading margin="0 0 4px">{drawing.name || `Drawing #${drawing.id}`}</SectionHeading>
      <div style={{ fontSize: 12, color: theme.textLight, marginBottom: 12 }}>
        {defects.length} defect{defects.length === 1 ? '' : 's'} on this drawing - click a pin or a row to open it.{src ? ' Drag to pan, scroll to zoom.' : ''}
      </div>

      {src ? (
        <div
          ref={boxRef}
          {...handlers}
          style={{
            position: 'relative', display: 'block', width: '100%',
            border: `1px solid ${theme.border}`, borderRadius: 6,
            overflow: 'hidden', background: theme.bg,
            ...surfaceStyle, cursor: surfaceStyle.cursor || 'default',
          }}>
          <div style={{
            transform,
            transformOrigin: '0 0',
            transition: dragging ? 'none' : 'transform 0.28s ease',
            position: 'relative', width: '100%',
          }}>
            <img src={src} alt={drawing.name || ''} loading="eager" draggable={false}
              onLoad={(e) => { const im = e.currentTarget; if (im.naturalWidth > 0) setNatural({ w: im.naturalWidth, h: im.naturalHeight }); }}
              style={{ display: 'block', width: '100%', height: 'auto', userSelect: 'none', pointerEvents: 'none' }} />
            {markupDoc && markupDoc.count > 0 && natural && boxW > 0 && (
              <MarkupLayer
                readOnly
                m={markupDoc}
                naturalW={natural.w}
                naturalH={natural.h}
                scale={boxW / natural.w}
                tx={0}
                ty={0}
                outerScale={view.scale}
                clientToImagePx={() => null}
              />
            )}
            {defects.map((d) => {
              if (typeof d.markX !== 'number' || typeof d.markY !== 'number') return null;
              const isSel = selectedPinId === d.id;
              const isHot = highlightId === d.id || isSel;
              // v0.291.0 — the selected pin is the report's accent (deep enough for its white number)
              const colour = isSel ? 'var(--report-pin)' : tradeColor(d.trade);
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => onPinClick(d)}
                  title={`#${d.defectNumber} ${d.description || ''}`}
                  className={isHot ? 'dp-flash' : ''}
                  style={{
                    position: 'absolute',
                    left: `${d.markX * 100}%`, top: `${d.markY * 100}%`,
                    transform: 'translate(-12px, -56px)',
                    width: 40, height: 60,
                    padding: 0, border: 'none', cursor: 'pointer',
                    background: 'transparent',
                    filter: isHot ? 'drop-shadow(0 0 5px color-mix(in srgb, var(--report-mark) 90%, transparent))' : 'none',
                    transition: 'filter 0.15s',
                  }}
                >
                  <svg width="40" height="60" viewBox="0 0 40 60" style={{ overflow: 'visible', display: 'block' }} aria-hidden>
                    <ellipse cx="12" cy="56" rx="7" ry="1.8" fill="rgba(0,0,0,0.35)" />
                    <line x1="12" y1="55" x2="22" y2="24" stroke="#5b6470" strokeWidth="2.6" strokeLinecap="round" />
                    <line x1="12.5" y1="54" x2="21.5" y2="24" stroke="rgba(255,255,255,0.35)" strokeWidth="0.8" strokeLinecap="round" />
                    <circle cx="22" cy="18" r={isHot ? 13 : 12} style={{ fill: colour }} stroke={isHot ? '#fff' : 'rgba(0,0,0,0.25)'} strokeWidth={isHot ? 2.5 : 1} />
                    <ellipse cx="18" cy="13" rx="4.5" ry="2.8" fill="rgba(255,255,255,0.55)" />
                    <text x="22" y="22" textAnchor="middle" fontSize="10" fontWeight="700" fill="#fff" style={{ fontFamily: 'inherit' }}>{d.defectNumber}</text>
                  </svg>
                </button>
              );
            })}
          </div>
          {isZoomed && (
            <button
              type="button"
              onClick={resetView}
              aria-label="Reset zoom"
              style={{
                position: 'absolute', top: 8, right: 8, zIndex: 5,
                border: 'none', borderRadius: 6, padding: '6px 10px',
                background: 'rgba(15,23,42,0.78)', color: '#fff',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >Reset</button>
          )}
        </div>
      ) : (
        <div style={{ padding: 16, color: theme.textLight, fontSize: 12 }}>(No plan image attached.)</div>
      )}

      <div style={{ marginTop: 14 }}>
        <DefectList
          defects={defects}
          drawingsById={drawingsById}
          highlightId={highlightId}
          onOpen={onOpen}
        />
      </div>
    </div>
  );
}

function DefectList({ defects, drawingsById, highlightId, onOpen }) {
  if (!defects.length) {
    return <div style={{ fontSize: 12, color: theme.textLight, padding: '8px 0' }}>No defects.</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {defects.map((d) => (
        <DefectRow
          key={d.id}
          d={d}
          drawing={drawingsById.get(d.drawingId)}
          isHot={highlightId === d.id}
          onOpen={onOpen}
        />
      ))}
    </div>
  );
}

// What an item's row and its card both show.
function itemLook(d, drawing) {
  // v0.36.0 — mudmap pin matches the big-plan pin (trade-coloured).
  const statusColour = tradeColor(d.trade);
  // v0.110.0 — show the recipient's (InSite's) priority when they've pushed one,
  // so the client sees the updated priority from the InSpec list; else their own.
  const shownPriority = d.recipientPriority || d.priority || 'medium';
  const priorityColour = shownPriority === 'high' ? theme.danger : shownPriority === 'low' ? theme.textLight : theme.warn;
  const hasPlanThumb = !!(drawing && (drawing.rasterUrl || drawing.sourceUrl) && typeof d.markX === 'number' && typeof d.markY === 'number');
  const comments = Array.isArray(d.comments) ? d.comments : [];
  // v0.264.0 - a photo sent in the chat shows in its message, not here as well.
  // v0.279.0 - and a marked-up photo the office posted.
  const chatPhotoIds = new Set(comments.filter(c => (c.authorRole === 'guest' || c.authorRole === 'author') && c.photoId != null).map(c => c.photoId));
  const allPhotos = Array.isArray(d.photos) ? d.photos : [];
  const photos = allPhotos.filter(p => !chatPhotoIds.has(p.id));
  return { statusColour, shownPriority, priorityColour, hasPlanThumb, comments, allPhotos, photos };
}

function PlanThumb({ d, drawing, colour, width, height }) {
  return (
    <>
      <img
        src={drawing.rasterUrl || drawing.sourceUrl}
        alt=""
        loading="lazy"
        decoding="async"
        style={{ display: 'block', width, height, objectFit: 'cover' }}
      />
      <span
        style={{
          position: 'absolute',
          left: `${d.markX * 100}%`, top: `${d.markY * 100}%`,
          transform: 'translate(-50%,-100%) rotate(-45deg)',
          width: 14, height: 14, borderRadius: '50% 50% 50% 0',
          background: colour, border: '1.5px solid white',
          boxShadow: '0 1px 2px rgba(0,0,0,0.4)',
        }}
      />
    </>
  );
}

function ItemBadges({ d, look }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 24, height: 22, padding: '0 7px', borderRadius: 11,
        background: look.statusColour, color: 'white',
        fontWeight: 800, fontSize: 11,
      }}>#{d.defectNumber}</span>
      <Pill colour={look.statusColour}>{statusLabel(d.status)}</Pill>
      {/* v0.287.0 — said in words: "Medium priority (set by …)" rather than
          "MEDIUM · …", which read as a person's name stuck on a level. */}
      <Pill colour={look.priorityColour} sentence>
        {priorityWords(look.shownPriority)}{d.recipientPriority ? ` (set by ${d.recipientName || 'the recipient'})` : ''}
      </Pill>
      {/* v0.110.0 — the recipient's progress on this item, pushed back from
          whatever system received the report. v0.114.0 — the label is the
          RecipientName the feedback payload carried, never a hardcoded
          company: DropPin is sold standalone and the recipient could be
          anyone. v0.287.0 — "… has it · Received" / "· Booked 3 Nov". */}
      {d.recipientStatus && (
        <Pill colour={theme.brand} sentence>
          {recipientProgressWords(d)}
        </Pill>
      )}
    </div>
  );
}

function ItemMeta({ d, messages = 0 }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 11, color: theme.textLight }}>
      {d.locationLabel && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><MapPin size={10} />{d.locationLabel}</span>}
      {d.assignedTo && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><User size={10} />{d.assignedTo}</span>}
      {d.createdAt && <span>Raised {fmtDate(d.createdAt)}</span>}
      {d.rectifiedAt && <span>✓ Rectified {fmtDate(d.rectifiedAt)}</span>}
      {d.completedAt && <span>✓✓ Complete {fmtDate(d.completedAt)}</span>}
      {messages > 0 && (
        <span data-part="message-count" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          <MessageSquare size={10} aria-hidden />{messages} message{messages === 1 ? '' : 's'}
        </span>
      )}
    </div>
  );
}

// v0.297.0 - Brett: "i though you had changed the client report so it had the
// popup chat like the app?" An item is a ROW, as in the app's list: plan
// thumbnail, words and photos, read at a glance, and pressing anywhere on it
// opens the item's card (ItemPopup) with its conversation. Nothing inside the
// row is a button of its own, so a press never does two things.
// v0.216.0 — the row WRAPS on a phone: the text asks for at least 160 px and
// the photos drop to their own line when the row cannot give it that.
// v0.287.0 — Brett: "their rows can be a bit gigger so the height of the 4
// photos". The 2x2 block stays, readable without opening the item.
function DefectRow({ d, drawing, isHot, onOpen }) {
  const look = itemLook(d, drawing);
  const photos = pairFirst(look.photos).ordered;   // v0.317.0 - before, after first
  const open = () => onOpen(d.id);
  return (
    <div
      id={`defect-${d.id}`}
      role="button"
      tabIndex={0}
      aria-haspopup="dialog"
      aria-label={`Open #${d.defectNumber}${d.description ? ` - ${d.description}` : ''}`}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
      className={isHot ? 'dp-row dp-flash' : 'dp-row'}
      style={{
        position: 'relative',
        border: `${isHot ? '2px' : '1px'} solid ${isHot ? 'var(--report-mark)' : theme.border}`,
        borderRadius: 8,
        padding: 12, paddingRight: 36, background: theme.surface,
        display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start',
        cursor: 'pointer',
        transition: 'border-color 0.15s',
      }}>
      {/* v0.13.1 - Plan thumbnail LEFT (mirrors the PDF layout). */}
      {look.hasPlanThumb && (
        <div style={{
          flex: '0 0 auto', position: 'relative', overflow: 'hidden',
          border: `1px solid ${theme.border}`, borderRadius: 6, width: 96, height: 72,
        }}>
          <PlanThumb d={d} drawing={drawing} colour={look.statusColour} width="100%" height="100%" />
        </div>
      )}

      <div style={{ flex: '1 1 160px', minWidth: 0 }}>
        <ItemBadges d={d} look={look} />
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
          {d.description || <em style={{ color: theme.textLight }}>(no description)</em>}
        </div>
        <ItemMeta d={d} messages={look.comments.length} />
      </div>

      {/* v0.13.1 - Photos RIGHT (mirrors the PDF layout), 2x2 then "+N more". */}
      {photos.length > 0 && (
        <div style={{
          flex: '0 0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 6,
          width: 'min(220px, 100%)',
        }}>
          {photos.slice(0, 4).map((p) => (
            <div key={p.id} style={{ border: `1px solid ${theme.border}`, borderRadius: 4, overflow: 'hidden', aspectRatio: '1 / 1' }}>
              <img
                src={p.thumbnailUrl || p.url}
                alt={p.caption || ''}
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          ))}
          {photos.length > 4 && (
            <div style={{ gridColumn: '1 / span 2', fontSize: 10, color: theme.textLight, textAlign: 'center' }}>
              +{photos.length - 4} more
            </div>
          )}
        </div>
      )}

      <ChevronRight size={18} aria-hidden style={{
        position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: theme.textLight,
      }} />
    </div>
  );
}

// The item itself, in its card: everything the row shows, every photo, and
// what this link lets the reader do with it.
function ItemDetails({ d, drawing, onJump, onPhotoClick, canRectify, onRectifyClick, canComplete, onCompleteClick }) {
  const look = itemLook(d, drawing);
  const { pair, ordered, rest } = pairFirst(look.photos);
  return (
    <div data-part="item" style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
        {look.hasPlanThumb && (
          <button
            type="button"
            onClick={() => onJump(d)}
            title="Show on the big plan"
            aria-label="Show on the big plan"
            style={{
              flex: '0 0 auto', position: 'relative', overflow: 'hidden',
              padding: 0, background: 'transparent', border: `1px solid ${theme.border}`,
              borderRadius: 6, cursor: 'pointer', width: 128, height: 96,
            }}
          >
            <PlanThumb d={d} drawing={drawing} colour={look.statusColour} width="100%" height="100%" />
          </button>
        )}
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <ItemBadges d={d} look={look} />
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
            {d.description || <em style={{ color: theme.textLight }}>(no description)</em>}
          </div>
          <ItemMeta d={d} />
          {/* v0.15.0 - Mark rectified: the link allows it and the item is
              not already rectified or complete. */}
          {canRectify && isOutstanding(d.status) && (
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                onClick={() => onRectifyClick(d)}
                style={{
                  padding: '7px 14px', borderRadius: 6,
                  background: theme.brand, color: 'var(--brand-on, #fff)',
                  border: 'none', fontWeight: 600, fontSize: 12,
                  cursor: 'pointer',
                }}
              >✓ Mark rectified</button>
            </div>
          )}
          {/* v0.16.0 - Confirm complete: an owner link, on an item that has
              been rectified (nothing is signed off before then). */}
          {canComplete && d.status === 'rectified' && (
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                onClick={() => onCompleteClick(d)}
                style={{
                  padding: '7px 14px', borderRadius: 6,
                  background: theme.success, color: 'white',
                  border: 'none', fontWeight: 600, fontSize: 12,
                  cursor: 'pointer',
                }}
              >✓✓ Confirm complete</button>
            </div>
          )}
        </div>
      </div>

      {pair && (
        // v0.317.0 - the fix photo beside the photo it answers, taken from the
        // same spot through InSpec's ghost camera. The viewer steps them in
        // this order: before, after, then the rest.
        <div data-part="item-pair" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, maxWidth: 520, marginBottom: rest.length ? 8 : 0 }}>
          {[['before', 'Before', pair.before, 0], ['after', 'After', pair.after, 1]].map(([role, label, p, idx]) => (
            <button
              key={role}
              type="button"
              data-role={role}
              onClick={() => onPhotoClick(ordered, idx, d.id)}
              aria-label={`Open the ${label.toLowerCase()} photo`}
              style={{
                position: 'relative', padding: 0, border: `1px solid ${theme.border}`,
                borderRadius: 6, overflow: 'hidden', cursor: 'zoom-in',
                background: 'transparent', aspectRatio: '4 / 3',
              }}
            >
              <img
                src={p.thumbnailUrl || p.url}
                alt={p.caption || label}
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              <span style={{
                position: 'absolute', top: 6, left: 6, padding: '2px 8px', borderRadius: 4,
                background: role === 'after' ? theme.success : theme.text, color: theme.surface,
                fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
              }}>{label}</span>
            </button>
          ))}
        </div>
      )}

      {rest.length > 0 && (
        <div data-part="item-photos" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
          {rest.map((p, i) => {
            const idx = i + (ordered.length - rest.length);
            return (
            <button
              key={p.id}
              type="button"
              onClick={() => onPhotoClick(ordered, idx, d.id)}
              aria-label={p.caption ? `Open photo: ${p.caption}` : `Open photo ${idx + 1} of ${ordered.length}`}
              style={{
                padding: 0, border: `1px solid ${theme.border}`,
                borderRadius: 6, overflow: 'hidden', cursor: 'zoom-in',
                background: 'transparent', aspectRatio: '1 / 1',
              }}
            >
              <img
                src={p.thumbnailUrl || p.url}
                alt={p.caption || ''}
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StepButton({ onClick, disabled, label, why, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={why || label}
      style={{
        ...btnSecondary, fontSize: 12, padding: '5px 10px',
        display: 'inline-flex', alignItems: 'center', gap: 4,
        opacity: disabled ? 0.45 : 1, cursor: disabled ? 'default' : 'pointer',
      }}
    >{children}</button>
  );
}

// v0.297.0 - THE ITEM'S CARD, as the app opens one (DefectEditorSheet): the
// item on the left and its conversation on the right on a wide screen, the
// whole screen with the conversation under the item on a phone. Previous and
// Next walk the report in its own order without closing. The page behind is
// inert while it is open; Esc, the cross or the dimmed page closes it, except
// while a photo or the rectify / complete box is open over it (blockedRef),
// which takes Esc for itself.
function ItemPopup({ d, ordered, onStep, onClose, blockedRef, itemFormRef, hasDrawings, drawingsById, onJump, onPhotoClick, canRectify, onRectifyClick, canComplete, onCompleteClick }) {
  const wide = React.useContext(WideCtx);
  const reply = React.useContext(ReplyCtx) || {};
  const titleId = React.useId();
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const el = dialogRef.current;
    if (el) el.focus({ preventScroll: true });
    // The page behind keeps its place and does not scroll under the card.
    const body = document.body;
    const was = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
    const bar = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (bar > 0) body.style.paddingRight = `${bar}px`;
    // Capture phase: React 18 runs a microtask between window listeners, so a
    // photo closing on the same Esc would already have cleared blockedRef.
    function onKey(e) {
      if (blockedRef.current) return;
      if (e.key === 'Escape') { e.preventDefault(); onCloseRef.current(); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      // Tab goes round the card, never out to the page behind it.
      const box = dialogRef.current;
      const items = Array.from(box.querySelectorAll('button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'))
        .filter(n => !n.disabled && n.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const at = document.activeElement;
      if (!box.contains(at)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && (at === first || at === box)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && at === last) { e.preventDefault(); first.focus(); }
    }
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      body.style.overflow = was.overflow;
      body.style.paddingRight = was.paddingRight;
    };
  }, [blockedRef]);

  if (!d) return null;
  const drawing = drawingsById.get(d.drawingId);
  const comments = Array.isArray(d.comments) ? d.comments : [];
  const allPhotos = Array.isArray(d.photos) ? d.photos : [];
  const hasThread = comments.length > 0 || !!reply.canReply;
  const noun = hasDrawings ? 'defect' : 'item';
  const i = ordered.findIndex(x => x.id === d.id);
  const prev = i > 0 ? ordered[i - 1] : null;
  const next = i >= 0 && i < ordered.length - 1 ? ordered[i + 1] : null;
  const split = wide && hasThread;

  const details = (
    <ItemDetails
      d={d}
      drawing={drawing}
      onJump={onJump}
      onPhotoClick={onPhotoClick}
      canRectify={canRectify}
      onRectifyClick={onRectifyClick}
      canComplete={canComplete}
      onCompleteClick={onCompleteClick}
    />
  );
  const frame = wide
    ? {
      left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
      width: split ? 1120 : 720, maxWidth: '96vw',
      ...(split ? { height: 'min(92vh, 900px)' } : { maxHeight: '92vh' }),
      // v0.304.0 — Brett, dark mode: "not enough colour difference". An edge
      // and the theme's deep shadow set the box apart from the report behind.
      borderRadius: 12, border: '1px solid var(--border-strong)', boxShadow: 'var(--shadow-3)',
    }
    : { inset: 0, borderRadius: 0 };

  return createPortal(
    <>
      <div
        data-part="item-backdrop"
        onClick={() => { if (!blockedRef.current) onClose(); }}
        // v0.304.0 — black, not the dark surface's own navy, so the box reads in front.
        style={{ position: 'fixed', inset: 0, zIndex: 1700, background: 'rgba(0,0,0,0.6)' }}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-part="item-popup"
        data-convo={d.id}
        style={{
          position: 'fixed', zIndex: 1701, ...frame,
          background: theme.surface, color: theme.text,
          display: 'flex', flexDirection: 'column', overflow: 'hidden', outline: 'none',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          padding: '12px 16px', borderBottom: `1px solid ${theme.border}`, flexShrink: 0,
        }}>
          <h2 id={titleId} style={{ margin: 0, fontSize: 15, fontWeight: 800, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {hasDrawings ? 'Defect' : 'Item'} #{d.defectNumber}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <div data-part="item-stepper" style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 4 }}>
              <StepButton
                onClick={() => prev && onStep(prev.id)}
                disabled={!prev}
                label={`Previous ${noun}`}
                why={prev ? null : `This is the first ${noun}`}
              ><ChevronLeft size={14} aria-hidden /><span className="ss-step-word">Previous</span></StepButton>
              <span style={{ fontSize: 12, color: theme.textLight, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {i + 1} of {ordered.length}
              </span>
              <StepButton
                onClick={() => next && onStep(next.id)}
                disabled={!next}
                label={`Next ${noun}`}
                why={next ? null : `This is the last ${noun}`}
              ><span className="ss-step-word">Next</span><ChevronRight size={14} aria-hidden /></StepButton>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              title="Close"
              style={{
                width: 28, height: 28, borderRadius: 6, border: 0, padding: 0,
                background: 'transparent', color: theme.textLight, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}
            ><X size={18} /></button>
          </div>
        </div>

        {split ? (
          <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
            <div ref={itemFormRef} data-part="item-form" style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '14px 16px' }}>
              {details}
            </div>
            <div data-part="item-conversation" style={{
              width: 380, flexShrink: 0, minHeight: 0, borderLeft: `1px solid ${theme.border}`,
              padding: '14px 16px', display: 'flex', flexDirection: 'column',
            }}>
              <ReplyThread key={d.id} d={d} comments={comments} allPhotos={allPhotos} onPhotoClick={onPhotoClick} layout="fill" />
            </div>
          </div>
        ) : (
          <div data-part="item-body" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {details}
            {/* A block of its own, so the thread's own flex rule (a row across a
                card's foot) does not stretch it down this column. */}
            {hasThread && (
              <div>
                <ReplyThread key={d.id} d={d} comments={comments} allPhotos={allPhotos} onPhotoClick={onPhotoClick} layout="under" />
              </div>
            )}
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}

// v0.264.0 - REPLY LINKS. Brett: "it will basically be like messenger under
// each point." A 'reply' link lets the person it was sent to answer under
// each item with a message, photos, or both (/api/public-reply). It changes
// nothing else. The page holds no session, so what the cards need comes
// down on this context rather than through every list and section.
const ReplyCtx = React.createContext(null);
// v0.319.0 - 10 a message, picked or dragged on (was 3). Brett: "is there
// anything stopping me from dragging on 10 photos into the client chat".
const MAX_REPLY_PHOTOS = MAX_CHAT_PHOTOS;
const REPLY_NAME_KEY = 'droppin.replyName';

// v0.283.0 - Brett: "Different chat for subbies and clients." A link that
// reads both chats opens each item on the chat its newest message is in (the
// client's when there is none) until a tab is pressed.
// (CHAT_TABS and chatOf are ConversationParts', shared with the app.)
function tabFor(tabs, comments, id) {
  if (tabs && tabs[id]) return tabs[id];
  const list = Array.isArray(comments) ? comments : [];
  return list.length ? chatOf(list[list.length - 1]) : 'client';
}

// layout 'under' = a row of its own across the foot of the item card;
// 'panel' = the item's right-hand column (v0.273.0): the messages scroll
// inside it with the newest in view, the reply box at its foot.
// 'fill' (v0.297.0) = the popup card's right-hand column: as 'panel', but
// the messages take the column's height rather than stopping at 360 px.
// v0.290.0 - Brett, of this page beside the app: "Why isnt it like the app?"
// The heading, tabs, messages and reply box are now ConversationParts, the
// same the app draws. The reader's own messages are the outside card,
// labelled "You", on the left like every other; nothing sits on the right.
function ReplyThread({ d, comments: all, allPhotos, onPhotoClick, layout = 'under' }) {
  const reply = React.useContext(ReplyCtx) || {};
  const panel = layout === 'panel';
  const fill = layout === 'fill';
  const both = !!reply.bothChats;
  const tab = both ? tabFor(reply.tabs, all, d.id) : null;
  const comments = both ? all.filter(c => chatOf(c) === tab) : all;
  const listRef = useRef(null);
  // v0.276.0 - a message arriving live keeps the newest in view only while
  // the reader is already at the foot; scrolled up to read an older one, it
  // is left where it is. The reader's own send always goes to the foot.
  const stickRef = useRef(true);
  const lastId = comments.length ? comments[comments.length - 1].id : null;
  useEffect(() => {
    const el = listRef.current;
    if ((panel || fill) && el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [panel, fill, comments.length, lastId]);
  function onListScroll() {
    const el = listRef.current;
    if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }
  // v0.280.0 - the office's newest message, once on screen for a moment with
  // the page in front, is reported seen (PublicReportScreen reportSeen). Only
  // one newer than the item's mark is watched, so an open page sends nothing
  // after that. A report that failed tries again on a later sight, 30 s on,
  // at most three times.
  const seenApi = reply.seen || null;
  const officeId = newestOfficeId(comments);
  // v0.320.0 - a run of photos is one row, carrying its first message's id.
  const runs = photoRuns(comments);
  const officeRun = officeId == null ? null : runs.find(r => r.members.some(m => m.id === officeId));
  const officeRowId = officeRun ? officeRun.lead.id : officeId;
  const known = Math.max(Number(d.seenMark) || 0, seenApi ? seenApi.get(d.id) : 0);
  const [seenRetry, setSeenRetry] = useState(0);
  useEffect(() => {
    if (!seenApi || officeId == null || officeId <= known || seenRetry > 3) return undefined;
    const el = listRef.current && listRef.current.querySelector(`[data-msg="${officeRowId}"]`);
    if (!el) return undefined;
    let gone = false;
    let again = null;
    const stop = watchSeen(el, () => {
      seenApi.report(d.id, officeId).then(ok => {
        if (!ok && !gone) again = setTimeout(() => setSeenRetry(n => n + 1), 30000);
      });
    });
    return () => { gone = true; stop(); if (again) clearTimeout(again); };
  }, [seenApi && seenApi.report, d.id, officeId, officeRowId, known, seenRetry]);
  // v0.322.0 - "response required" both ways. Brett: "Yes they can flag
  // messages back to me. They should see flags that are on their chat" and,
  // of the office card's banner, "thats too much, remove it. just the redbox
  // is fine". So the mark is the red box on the message and nothing else. A
  // link that can reply flags from the message's ⋯ and clears only its own
  // flag; the office's stays until the office clears it. No names: the hover
  // says the office, you, or someone else.
  const [menuId, setMenuId] = useState(null);
  const [flagBusy, setFlagBusy] = useState(false);
  const [flagErr, setFlagErr] = useState(null);   // { id, msg }
  function flagHover(members) {
    const f = members.map(m => m.flag).filter(Boolean)
      .reduce((a, x) => (!a || String(x.at) > String(a.at) ? x : a), null);
    if (!f) return undefined;
    const who = f.office ? 'Flagged by the office' : f.mine ? 'Flagged by you' : 'Flagged';
    return f.at ? `${who} · ${fmtDate(f.at)}` : who;
  }
  async function sendFlag(leadId, targets, action) {
    if (flagBusy) return;
    setFlagErr(null);
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setFlagErr({ id: leadId, msg: 'Flagging needs a connection. Nothing was changed.' });
      return;
    }
    setFlagBusy(true);
    try {
      for (const id of targets) {
        const r = await fetch('/api/public-flag', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: reply.token, commentId: id, action }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `Could not ${action === 'clear' ? 'clear' : 'flag'} it (HTTP ${r.status}). Nothing was changed.`);
        if (reply.onFlagged) reply.onFlagged(d.id, id, j.flag || null);
      }
      setMenuId(null);
    } catch (e) {
      setFlagErr({ id: leadId, msg: e.message || 'Could not reach the server. Nothing was changed.' });
    } finally {
      setFlagBusy(false);
    }
  }
  const smallBtn = { ...btnSecondary, fontSize: 11, padding: '3px 8px' };
  const brand = reply.brand || {};
  // v0.278.0 - who else is on this item. v0.284.0 - their badges head the
  // conversation; a both-chats link shows the people in the chat on screen
  // (the server already keeps a one-chat link to its own chat).
  const here = (reply.presence && reply.presence[d.id]) || [];
  const people = both ? here.filter(p => !p.chat || p.chat === tab) : here;
  if (!all.length && !reply.canReply && !people.length) return null;
  function openPhoto(photo) {
    const idx = allPhotos.findIndex(p => p.id === photo.id);
    if (idx >= 0) onPhotoClick(allPhotos, idx, d.id); else onPhotoClick([photo], 0, d.id);
  }
  return (
    <div data-part="conversation" style={{
      display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0,
      ...(fill
        ? { flex: 1, minHeight: 0 }
        : panel
          ? { alignSelf: 'stretch', borderLeft: `1px solid ${theme.border}`, paddingLeft: 14 }
          : { flex: '1 1 100%', borderTop: `1px solid ${theme.border}`, paddingTop: 10 }),
    }}>
      <ConversationLabel live={reply.live} people={people} logoUrl={brand.logoUrl}>Conversation</ConversationLabel>
      {both && (
        <ChatTabs
          current={tab}
          counts={Object.fromEntries(CHAT_TABS.map(([k]) => [k, all.filter(c => chatOf(c) === k).length]))}
          flagged={Object.fromEntries(CHAT_TABS.map(([k]) => [k, all.some(c => chatOf(c) === k && !c.removed && c.flag)]))}
          onPick={k => { stickRef.current = true; reply.setTab(d.id, k); }}
        />
      )}
      {(panel || fill || both) && !comments.length && (
        <div data-chat-empty style={{ fontSize: 12, color: theme.textLight }}>
          {both ? `No messages with ${tab === 'trade' ? 'the subcontractors' : 'the client'} yet.` : 'No messages on this item yet.'}
        </div>
      )}
      {comments.length > 0 && (
        <ThreadBox ref={listRef} onScroll={onListScroll} data-part="messages" fill={fill} maxHeight={panel ? 360 : null}>
          {runs.map(({ lead: c, members }) => {
            const mine = c.authorRole === 'guest' && reply.shareId != null && c.viaShareId === reply.shareId;
            // v0.289.0 - a message the office removed: the server sends
            // "Removed by <company>" in its place and no photo.
            const removed = !!c.removed;
            const photo = removed ? null : (c.photo || null);
            const showBody = c.body && !(photo && c.body === '(photo)');
            // v0.322.0 - flagged, and what this link may do about it.
            const flaggedMembers = removed ? [] : members.filter(m => m.flag);
            const flagged = flaggedMembers.length > 0;
            const canFlag = !flagged;
            const canClear = flagged && flaggedMembers.every(m => m.flag.mine);
            const hasMenu = !!reply.canReply && !removed && (canFlag || canClear);
            const open = hasMenu && menuId === c.id;
            const err = flagErr && flagErr.id === c.id ? flagErr.msg : null;
            return (
              <MessageRow
                key={c.id}
                id={c.id}
                outsider={isOutsider(c)}
                removed={removed}
                flagged={flagged}
                extra={flagged ? <FlagTag title={flagHover(members)} /> : null}
                action={hasMenu ? (
                  <MessageMenuButton
                    data-msg-menu={c.id}
                    open={open}
                    title={canClear ? 'Clear flag' : 'Flag'}
                    onClick={() => { setMenuId(open ? null : c.id); setFlagErr(null); }}
                  />
                ) : null}
                avatar={<MessageAvatar c={c} brand={brand} mine={mine} />}
                label={messageLabel(c, mine)}
                date={c.createdAt}
              >
                {showBody && (removed
                  ? <MessageText tone="removed" data-removed>{c.body}</MessageText>
                  : <MessageText>{c.body}</MessageText>)}
                {members.length > 1 ? (
                  <MessagePhotos items={members.map((m, i) => ({
                    key: m.id,
                    photo: m.photo,
                    onOpen: () => onPhotoClick(members.map(x => x.photo), i, d.id),
                  }))} />
                ) : photo && <MessagePhoto photo={photo} onOpen={() => openPhoto(photo)} />}
                {open && (
                  <div data-flag-menu={c.id} style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${theme.border}`, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {canClear ? (
                      <button type="button" data-flag="clear" disabled={flagBusy}
                        onClick={() => sendFlag(c.id, flaggedMembers.map(m => m.id), 'clear')}
                        style={smallBtn}>{flagBusy ? 'Clearing…' : 'Clear flag'}</button>
                    ) : (
                      <button type="button" data-flag="flag" disabled={flagBusy}
                        onClick={() => sendFlag(c.id, [c.id], 'flag')}
                        style={{ ...smallBtn, color: theme.danger, borderColor: theme.danger, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Flag size={12} aria-hidden="true" />{flagBusy ? 'Flagging…' : 'Flag: response required'}
                      </button>
                    )}
                  </div>
                )}
                {err && <div data-flag-err={c.id} role="alert" style={{ marginTop: 4, fontSize: 11, fontWeight: 600, color: theme.danger }}>{err}</div>}
              </MessageRow>
            );
          })}
        </ThreadBox>
      )}
      <PresenceNote people={people} />
      {/* v0.283.0 - one box per chat, the other kept hidden with its words,
          so nothing typed to one chat is sent to the other by a tab press. */}
      {reply.canReply && (both
        ? CHAT_TABS.map(([k]) => (
          <div key={k} hidden={k !== tab}>
            <ReplyComposer d={d} chat={k} panel={panel || fill} onSent={() => { stickRef.current = true; }} />
          </div>
        ))
        : <ReplyComposer d={d} panel={panel || fill} onSent={() => { stickRef.current = true; }} />)}
    </div>
  );
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read that photo file'));
    reader.readAsDataURL(file);
  });
}

// v0.297.0 - one box at a time is on screen now (the open item's card), so
// what is typed or picked is kept on the page per item and chat (reply.drafts)
// and is back when Next or Previous returns to the item, or it is reopened.
// The photo keys are page-wide (utils/chatPhotos) so two items' photos never share one.

function ReplyComposer({ d, chat = null, panel = false, onSent }) {
  const reply = React.useContext(ReplyCtx);
  const draftKey = `${d.id}:${chat || ''}`;
  const draft = (reply.drafts && reply.drafts.get(draftKey)) || null;
  const [text, setText] = useState(() => (draft ? draft.text : ''));
  const [pics, setPics] = useState(() => (draft ? draft.pics : []));   // [{ key, dataUrl }]
  const [name, setName] = useState(() => {
    if (reply.replyName) return reply.replyName;
    try { return localStorage.getItem(REPLY_NAME_KEY) || ''; } catch { return ''; }
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    const drafts = reply.drafts;
    if (!drafts) return;
    if (text || pics.length) drafts.set(draftKey, { text, pics });
    else drafts.delete(draftKey);
  }, [reply.drafts, draftKey, text, pics]);

  // v0.319.0 - picked with Add photos or dragged onto the box: the same way
  // in (utils/chatPhotos: an iPhone HEIC becomes a JPEG, a typeless Windows
  // drag is read for what it is, a PDF is named and left out).
  const [preparing, setPreparing] = useState(0);
  async function addFiles(files) {
    if (busy || !files.length) return;
    setErr(null);
    setPreparing(n => n + 1);
    try {
      const got = await photosForChat(files, MAX_REPLY_PHOTOS - pics.length);
      if (got.pics.length) setPics(prev => [...prev, ...got.pics].slice(0, MAX_REPLY_PHOTOS));
      const msg = photosForChatError(got, MAX_REPLY_PHOTOS);
      if (msg) setErr(msg);
    } finally {
      setPreparing(n => Math.max(0, n - 1));
    }
  }

  async function send() {
    const body = text.trim();
    if (busy || (!body && !pics.length)) return;
    const who = name.trim();
    if (reply.needName && !who) { setErr('Add your name so they know who replied.'); return; }
    setErr(null);
    setBusy(true);
    if (who) { try { localStorage.setItem(REPLY_NAME_KEY, who); } catch { /* private window */ } }
    // One message per photo: the words go with the first. Whatever is sent
    // leaves the box at once, so a failure part-way never sends it twice.
    const sends = pics.length ? pics.map((p, i) => ({ text: i === 0 ? body : '', pic: p })) : [{ text: body, pic: null }];
    let sent = 0;
    try {
      for (const s of sends) {
        let photo = {};
        if (s.pic) {
          const v = await makeImageVariants(s.pic.dataUrl);
          photo = { photoUrl: s.pic.dataUrl, photoThumbnailUrl: v.thumbnailUrl, photoDisplayUrl: v.displayUrl };
        }
        const r = await fetch('/api/public-reply', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: reply.token, defectId: d.id, body: s.text, authorName: who || null, ...photo, ...(chat ? { chat } : {}) }),
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.error || `HTTP ${r.status}`);
        }
        const json = await r.json();
        if (onSent) onSent();
        reply.onReplied(d.id, json.comment, json.photo);
        sent++;
        if (s.text) setText('');
        if (s.pic) setPics(prev => prev.filter(p => p.key !== s.pic.key));
      }
    } catch (e) {
      setErr((sent ? `Sent ${sent} of ${sends.length}. ` : '') + (e.message || String(e)) + (sent ? '' : ' Nothing was sent.'));
    } finally {
      setBusy(false);
    }
  }

  const canSend = !busy && !preparing && !!(text.trim() || pics.length);
  const full = busy || pics.length >= MAX_REPLY_PHOTOS;
  // The same reply box as the app's; what only this page has - the name box
  // on a link with no email, the photos waiting to go and Add photos - comes
  // in through its slots.
  return (
    <Composer
      value={text}
      onChange={v => { setText(v); if (reply.onTyping) reply.onTyping(d.id, chat); }}
      onSend={send}
      sending={busy}
      canSend={canSend}
      rows={panel ? 3 : 2}
      placeholder={chat
        ? `Write to ${chat === 'trade' ? 'the subcontractors' : 'the client'}${reply.speakerName ? ` as ${reply.speakerName}` : ''}…`
        : reply.speakerName ? `Reply as ${reply.speakerName}…` : 'Write a reply…'}
      above={reply.needName ? (
        <input
          type="text"
          value={name}
          onChange={e => { setName(e.target.value); if (reply.onName) reply.onName(e.target.value); }}
          placeholder="Your name"
          aria-label="Your name"
          maxLength={120}
          style={{ ...inputStyle, maxWidth: 260 }}
        />
      ) : null}
      attachments={<PhotoAttachments pics={pics} busy={busy} onRemove={key => setPics(prev => prev.filter(x => x.key !== key))} />}
      tools={<AddPhotosButton onFiles={addFiles} max={MAX_REPLY_PHOTOS} full={full} note={preparing ? 'Preparing photos…' : pics.length ? `${pics.length} of ${MAX_REPLY_PHOTOS}` : ''} />}
      onFiles={addFiles}
      error={err || ''}
    />
  );
}

/**
 * v0.218.0 — THE SENDER'S PDF, FOR THE RECIPIENT. Brett: "what we produce is
 * what they get. The html version is preference as its clickable however
 * sometimes a pdf is needed."
 *
 * Built in this browser from the live report on this page, by the same code
 * and rules the sender's report screen uses (utils/reportPdf.js): the settings
 * the sender shared with, their plan level (watermark, white-label brand) as
 * recorded from their session, and their tenant's brand. Everything heavy is
 * loaded only when a PDF is wanted, so the page itself stays light.
 *
 * v0.292.0 — the build moved to useReportPdf (utils/clientReportPdf.js), one
 * path with "View as PDF": the bytes this button saves are the bytes the PDF
 * view draws, and a PDF already built for one is handed to the other.
 */
function DownloadReportPdf({ pdf, onPaper = false, quiet = false }) {
  const [saving, setSaving] = useState(false);
  const busy = saving || pdf.status === 'building';
  const run = async () => {
    if (busy) return;
    setSaving(true);
    try { await pdf.download(); } catch (_) { /* pdf.error says why, below */ } finally { setSaving(false); }
  };
  const problems = pdf.problems.length;
  // quiet: in the PDF view the failure is told once, by the view's own panel with its Retry
  const note = pdf.error
    ? (quiet ? null : 'The PDF could not be made: ' + pdf.error + '. Try again, or use the report on this page.')
    : problems
      ? `${problems === 1 ? 'One picture' : problems + ' pictures'} could not be downloaded; the PDF says so where they belong.`
      : null;
  // on the report's cover the button wears the cover's paper (v0.291.0);
  // in the PDF view it sits on the page and keeps the suite's button
  const paper = onPaper ? {
    background: 'var(--report-chip-bg)', color: 'var(--report-paper-text)',
    border: '1px solid var(--report-paper-line)',
  } : null;
  const muted = onPaper ? 'var(--report-paper-muted)' : theme.textLight;
  const text = onPaper ? 'var(--report-paper-text)' : theme.text;
  return (
    <div style={{ marginTop: onPaper ? 14 : 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <button type="button" onClick={run} disabled={busy} data-download-pdf
        style={{
          ...btnSecondary, ...paper,
          display: 'inline-flex', alignItems: 'center', gap: 6, opacity: busy ? 0.6 : 1,
        }}>
        <Download size={14} /> {busy ? 'Making PDF…' : 'Download PDF'}
      </button>
      {busy && pdf.progress && pdf.progress.total > 0 && (
        <span style={{ fontSize: 12, color: muted }}>Getting photos and plans — {pdf.progress.done} of {pdf.progress.total}</span>
      )}
      {!busy && note && <span style={{ fontSize: 12, color: text }}>{note}</span>}
    </div>
  );
}

// ----------------------------------------------------------------------
// v0.292.0 — VIEW AS PDF. Brett (v0.218): "what we produce is what they get.
// The html version is preference as its clickable however sometimes a pdf is
// needed." The client can now READ the PDF in the page (Web page | PDF in the
// header) without downloading it; Download saves the same bytes.
//
// useReportPdf: the one build for both. A finished build is kept while the
// PDF's inputs are unchanged (reportPdfFingerprint — the settings, brand,
// project, items and today's date; never the chat), so toggling back and
// forth reuses it and a reply arriving does not rebuild it. A second asker
// joins the build already running. A failed build is dropped so Retry tries
// again. Status is one of idle / building / ready / error — named, never
// inferred from a missing value.
function useReportPdf(data) {
  // The date the PDF prints is part of its inputs. It is fixed here, when the
  // report arrives or changes, and the build uses the SAME clock, so the
  // fingerprint the page compares against and the one the build carries can
  // never disagree (a tab left open past midnight would otherwise wait for a
  // build that had already finished).
  const stamp = useMemo(() => {
    const now = new Date();
    return data ? { fingerprint: reportPdfFingerprint(data, now), now } : null;
  }, [data]);
  const fingerprint = stamp ? stamp.fingerprint : null;
  const [built, setBuilt] = useState(null);       // { fingerprint, bytes, fileName, problems, hash }
  const [building, setBuilding] = useState(null); // the fingerprint being built
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);       // { fingerprint, message }
  const dataRef = useRef(data); dataRef.current = data;
  const stampRef = useRef(stamp); stampRef.current = stamp;
  const builtRef = useRef(null);
  const inflight = useRef(null);                  // { fingerprint, promise }
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const ensure = useCallback(() => {
    const d = dataRef.current;
    const s = stampRef.current;
    if (!d || !s) return Promise.reject(new Error('The report has not loaded yet'));
    const fp = s.fingerprint;
    if (builtRef.current && builtRef.current.fingerprint === fp) return Promise.resolve(builtRef.current);
    if (inflight.current && inflight.current.fingerprint === fp) return inflight.current.promise;
    setBuilding(fp); setError(null); setProgress(null);
    const promise = buildClientReportPdf(d, { now: s.now, onProgress: (p) => { if (alive.current) setProgress(p); } }).then(
      (out) => {
        if (inflight.current && inflight.current.promise === promise) inflight.current = null;
        builtRef.current = out;
        if (alive.current) { setBuilt(out); setBuilding((b) => (b === fp ? null : b)); setProgress(null); }
        return out;
      },
      (e) => {
        if (inflight.current && inflight.current.promise === promise) inflight.current = null;
        console.error('[public-report] PDF failed', e);
        if (alive.current) {
          setError({ fingerprint: fp, message: (e && e.message) || 'unknown error' });
          setBuilding((b) => (b === fp ? null : b)); setProgress(null);
        }
        throw e;
      },
    );
    inflight.current = { fingerprint: fp, promise };
    return promise;
  }, []);

  const download = useCallback(() => ensure().then((o) => { downloadPdfBytes(o.bytes, o.fileName); return o; }), [ensure]);

  const ready = built && built.fingerprint === fingerprint ? built : null;
  const status = ready ? 'ready'
    : building === fingerprint ? 'building'
      : error && error.fingerprint === fingerprint ? 'error'
        : 'idle';
  return {
    status,
    fingerprint,
    bytes: ready ? ready.bytes : null,
    hash: ready ? ready.hash : null,
    fileName: ready ? ready.fileName : null,
    problems: ready ? ready.problems : [],
    progress: status === 'building' ? progress : null,
    error: status === 'error' ? error.message : null,
    ensure,
    retry: ensure,
    download,
  };
}

// Which view is on screen, kept in the URL as ?view=pdf so a link can open
// straight on the PDF. Switching REPLACES the entry (no history spam: Back
// still leaves the page). Each view's scroll position is kept and restored.
const VIEW_PARAM = 'view';
function readView() {
  try { return new URLSearchParams(window.location.search).get(VIEW_PARAM) === 'pdf' ? 'pdf' : 'web'; } catch { return 'web'; }
}
function useReportView() {
  const [view, setViewState] = useState(readView);
  const viewRef = useRef(view);
  const scrollRef = useRef({ web: 0, pdf: 0 });
  const switched = useRef(false);
  const setView = useCallback((next) => {
    const v = next === 'pdf' ? 'pdf' : 'web';
    const cur = viewRef.current;
    if (cur === v) return;
    try { scrollRef.current[cur] = window.scrollY; } catch { /* no window */ }
    try {
      const url = new URL(window.location.href);
      if (v === 'pdf') url.searchParams.set(VIEW_PARAM, 'pdf'); else url.searchParams.delete(VIEW_PARAM);
      window.history.replaceState(window.history.state, '', url.toString());
    } catch { /* the address stays as it was; the view still switches */ }
    viewRef.current = v;
    switched.current = true;
    setViewState(v);
  }, []);
  useEffect(() => {
    if (!switched.current) return undefined; // first paint: leave #defect-N anchors alone
    const y = scrollRef.current[view] || 0;
    const id = requestAnimationFrame(() => { try { window.scrollTo(0, y); } catch { /* no window */ } });
    return () => cancelAnimationFrame(id);
  }, [view]);
  return [view, setView];
}

// The PDF's pages, drawn in the page by pdf.js (the same engine DropPin
// rasterises plan sheets with - utils/pdfRaster.js). Pages are fitted to the
// column, drawn at the screen's pixel density (to 2x, within the shared canvas
// budget), rendered as they near the viewport and released again when far
// away, so a 40-page report on an iPad holds a handful of canvases, not forty.
const PDF_PAGE_MAX_PX = 900;
const PDF_PAGE_GAP = 16;
function ReportPdfView({ pdf }) {
  const { status, bytes, hash, ensure } = pdf;
  // The PDF on screen: { hash, size, doc, numPages, aspect }. When the office
  // changes an item while the PDF is open, the new PDF is built and opened
  // BEHIND this one, then each page swaps in place - the reader keeps their
  // page instead of the view emptying and starting again from the top.
  const [doc, setDoc] = useState(null);
  const docRef = useRef(null);
  const [openErr, setOpenErr] = useState(null);
  const [openTry, setOpenTry] = useState(0);
  const colRef = useRef(null);
  const [colW, setColW] = useState(0);
  const queue = useRef(Promise.resolve());
  const enqueue = useCallback((job) => {
    const next = queue.current.then(job).catch((e) => {
      if (!(e && (e.name === 'RenderingCancelledException' || /cancel/i.test(String(e && e.message))))) console.error('[public-report] PDF page failed', e);
    });
    queue.current = next;
    return next;
  }, []);

  // the engine first (the worker is an asset of its own), then the build
  useEffect(() => { warmPdfEngine(); }, []);
  useEffect(() => { if (status === 'idle') ensure().catch(() => {}); }, [status, ensure]);

  // open the finished bytes. While a new build is made (no bytes yet) the PDF
  // already shown stays; once the new one is open it replaces it, and the old
  // one is closed behind any page still drawing from it (the queue is serial).
  useEffect(() => {
    setOpenErr(null);
    if (!bytes) return undefined;
    if (docRef.current && docRef.current.hash === hash) return undefined;
    let cancelled = false;
    openPdf(bytes).then(async (d) => {
      if (cancelled) { d.destroy(); return; }
      const first = await d.getPage(1);
      const vp = first.getViewport({ scale: 1 });
      if (cancelled) { d.destroy(); return; }
      const old = docRef.current;
      docRef.current = { hash, size: bytes.byteLength, doc: d, numPages: d.numPages, aspect: vp.height / vp.width };
      setDoc(docRef.current);
      if (old) enqueue(() => old.doc.destroy());
    }).catch((e) => {
      if (cancelled) return;
      console.error('[public-report] PDF open failed', e);
      setOpenErr((e && e.message) || 'unknown error');
    });
    return () => { cancelled = true; };
  }, [bytes, hash, openTry, enqueue]);
  useEffect(() => () => { if (docRef.current) docRef.current.doc.destroy(); }, []);

  // the column's width - pages fit it
  useEffect(() => {
    const el = colRef.current;
    if (!el) return undefined;
    const measure = () => setColW(Math.floor(el.getBoundingClientRect().width));
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pageW = Math.max(0, Math.min(colW, PDF_PAGE_MAX_PX));
  const panel = { ...card, padding: 28, textAlign: 'center', color: theme.textLight, fontSize: 13 };
  // With a PDF on screen: ready (it is the current build), updating (a newer
  // one is being made or opened behind it) or stale (that update failed - the
  // pages stay, the line says they are out of date, Retry tries again).
  // Without one: building, opening or error, each a panel of its own.
  const view = doc
    ? (status === 'error' || openErr ? 'stale' : status === 'ready' && doc.hash === hash ? 'ready' : 'updating')
    : (status === 'error' || openErr ? 'error' : status === 'ready' ? 'opening' : 'building');
  const pages = doc ? doc.numPages : 0;
  const retry = () => { if (openErr) setOpenTry((n) => n + 1); else pdf.retry().catch(() => {}); };
  const pageCount = `${pages} ${pages === 1 ? 'page' : 'pages'}`;

  return (
    <div ref={colRef} data-pdf-view data-status={view}
      data-pdf-pages={doc ? pages : undefined}
      data-pdf-bytes={doc ? doc.size : undefined}
      data-pdf-hash={doc ? doc.hash : undefined}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: PDF_PAGE_GAP, minWidth: 0 }}>
      <div style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
        fontSize: 12, color: theme.textLight,
      }}>
        {view === 'stale' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span key="stale" data-pdf-count role="alert" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: theme.text }}>
              <AlertTriangle size={14} color={theme.warn} strokeWidth={2} />
              This PDF is out of date — the update could not be made
            </span>
            <button type="button" data-pdf-retry onClick={retry}
              style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        ) : (
          <span key="count" data-pdf-count aria-live="polite">
            {view === 'ready' ? pageCount
              : view === 'updating' ? `${pageCount} · Updating the PDF…`
                : view === 'building' ? 'Making the PDF…' : view === 'opening' ? 'Opening the PDF…' : 'The PDF could not be shown'}
          </span>
        )}
        <DownloadReportPdf pdf={pdf} quiet />
      </div>

      {view === 'building' && (
        <div role="status" style={{ ...panel, width: '100%' }}>
          <div style={{ fontWeight: 700, color: theme.text, marginBottom: 4 }}>Making the PDF…</div>
          {pdf.progress && pdf.progress.total > 0
            ? <div>Getting photos and plans — {pdf.progress.done} of {pdf.progress.total}</div>
            : <div>The report's pages are being laid out in this browser.</div>}
        </div>
      )}
      {view === 'opening' && (
        <div role="status" style={{ ...panel, width: '100%' }}>Opening the PDF…</div>
      )}
      {view === 'error' && (
        <div role="alert" style={{ ...panel, width: '100%' }}>
          <AlertTriangle size={28} color={theme.warn} strokeWidth={1.5} style={{ marginBottom: 6 }} />
          <div style={{ fontWeight: 700, color: theme.text, marginBottom: 4 }}>The PDF could not be {openErr ? 'shown' : 'made'}</div>
          <div style={{ marginBottom: 12 }}>{openErr || pdf.error}</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" data-pdf-retry onClick={retry}
              style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <RefreshCw size={14} /> Retry
            </button>
          </div>
          <div style={{ marginTop: 10 }}>Or read the report as a web page — the switch is at the top.</div>
        </div>
      )}
      {/* keyed by page number alone: a new build redraws each page where it is */}
      {doc && pageW > 0 && Array.from({ length: pages }, (_, i) => (
        <PdfPage key={i + 1} doc={doc.doc} docHash={doc.hash} n={i + 1} total={pages} width={pageW} aspect={doc.aspect} enqueue={enqueue} />
      ))}
    </div>
  );
}

// One page: a placeholder of the page's shape until it nears the viewport,
// then a canvas drawn at the screen's density; dropped again when far away.
// A page already showing is redrawn off screen and swapped in whole, so a new
// build or a resize never blanks the page being read.
const PDF_NEAR = '1200px 0px';
function PdfPage({ doc, docHash, n, total, width, aspect, enqueue }) {
  const ref = useRef(null);
  const canvasRef = useRef(null);
  const [near, setNear] = useState(false);
  const [drawn, setDrawn] = useState(null); // { doc, width, dpr } of the paint on screen

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setNear(true); return undefined; }
    const io = new IntersectionObserver((entries) => { for (const e of entries) setNear(e.isIntersecting); }, { rootMargin: PDF_NEAR });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    if (!near) {
      // far away: give the memory back; the placeholder keeps the page's place
      if (drawn) {
        canvas.width = 0; canvas.height = 0;
        delete canvas.dataset.rendered; delete canvas.dataset.paintOf;
        setDrawn(null);
      }
      return undefined;
    }
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (drawn && drawn.doc === doc && drawn.width === width && drawn.dpr === dpr) return undefined;
    let cancelled = false;
    let task = null;
    enqueue(async () => {
      if (cancelled) return;
      const page = await doc.getPage(n);
      if (cancelled) return;
      const natural = page.getViewport({ scale: 1 });
      const scale = clampScale((width / natural.width) * dpr, natural);
      const vp = page.getViewport({ scale });
      const c = canvasRef.current;
      if (!c) return;
      const w = Math.round(vp.width);
      const h = Math.round(vp.height);
      const off = c.dataset.rendered ? document.createElement('canvas') : null;
      const target = off || c;
      try {
        target.width = w; target.height = h;
        task = page.render({ canvasContext: target.getContext('2d'), viewport: vp });
        await task.promise;
        task = null;
        if (cancelled) return;
        if (off) { c.width = w; c.height = h; c.getContext('2d').drawImage(off, 0, 0); }
      } finally {
        if (off) { off.width = 0; off.height = 0; } // its memory back at once, not at the next collection
      }
      c.dataset.rendered = '1';
      c.dataset.paintOf = docHash;
      setDrawn({ doc, width, dpr });
    });
    return () => { cancelled = true; if (task) task.cancel(); };
  }, [near, doc, n, width, enqueue]); // eslint-disable-line react-hooks/exhaustive-deps

  const height = Math.round(width * aspect);
  return (
    <div ref={ref} data-pdf-slot={n} style={{ width, maxWidth: '100%', flex: 'none' }}>
      <div style={{
        width, height, background: 'var(--surface-1)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-1)', boxShadow: 'var(--shadow-2)', overflow: 'hidden',
      }}>
        {/* pdf.js paints the page's own paper; until then the placeholder shows through */}
        <canvas ref={canvasRef} data-pdf-page={n} aria-label={`Page ${n} of ${total}`}
          style={{ display: 'block', width, height }} />
      </div>
      <div style={{ textAlign: 'center', fontSize: 11, color: theme.textLight, marginTop: 6 }}>{n} / {total}</div>
    </div>
  );
}

// sentence (v0.287.0) — a pill that says a phrase keeps its own case, so a
// company name is not shouted.
function Pill({ children, colour, sentence = false }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      fontSize: sentence ? 11 : 10, fontWeight: 700, textTransform: sentence ? 'none' : 'uppercase',
      // v0.291.0 — a 13% tint mixed, not '22' glued on: the colour may be a var()
      background: `color-mix(in srgb, ${colour} 13%, transparent)`, color: colour,
    }}>{children}</span>
  );
}

// v0.287.0 — "high" → "High priority".
function priorityWords(p) {
  const k = String(p || 'medium').toLowerCase();
  return k.charAt(0).toUpperCase() + k.slice(1) + ' priority';
}

// v0.287.0 — the receiving company's progress, in words. Received, in progress
// and booked are "has it"; done and declined say what happened.
function recipientProgressWords(d) {
  const who = d.recipientName || 'The recipient';
  const k = String(d.recipientStatus || '').toLowerCase();
  const booked = d.recipientBookedDate ? 'Booked ' + fmtDate(d.recipientBookedDate) : null;
  if (k === 'done') return `${who} · Done`;
  if (k === 'declined') return `${who} · Declined`;
  if (k === 'scheduled') return `${who} has it · ${booked || 'Booked'}`;
  const plain = k.replace(/_/g, ' ');
  const step = plain.charAt(0).toUpperCase() + plain.slice(1);
  return `${who} has it · ${step}${booked ? ' · ' + booked : ''}`;
}

// v0.15.0 - Rectify modal. The supervisor with a rectify-tier share
// link opens this from a defect card. They pick a photo (camera on
// mobile / file picker on desktop) + type a comment, hit Submit, and
// POST hits /api/public-rectify which validates the token, inserts
// the photo + comment, and flips the defect to 'rectified'.
function RectifyModal({ defect, token, shareRecipient, onClose, onSuccess, onError }) {
  const [photoFile, setPhotoFile] = useState(null);   // File object
  const [photoDataUrl, setPhotoDataUrl] = useState(null);  // for preview
  const [comment, setComment] = useState('');
  // v0.281.0 - a link sent to a person signs as that person; only a link
  // sent to nobody asks for a name.
  const [authorName, setAuthorName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function onPickFile(e) {
    const picked = e.target.files?.[0];
    if (!picked) return;
    setErr(null);
    // 2026-09-15 — an iPhone photo (HEIC) is converted to a JPEG, at the stored
    // size, before it is read (utils/heicPhotos): no other browser can show a HEIC.
    let f;
    try { f = await toUploadableImage(picked); } catch (convErr) { setErr(convErr?.message || 'Could not read that photo file'); return; }
    setPhotoFile(f);
    const reader = new FileReader();
    reader.onload = () => setPhotoDataUrl(String(reader.result));
    reader.onerror = () => setErr('Could not read that photo file');
    reader.readAsDataURL(f);
  }

  function clearPhoto() {
    setPhotoFile(null);
    setPhotoDataUrl(null);
  }

  async function submit() {
    setErr(null);
    if (!comment.trim()) { setErr('Add a short comment explaining the fix.'); return; }
    if (!photoDataUrl) { setErr('A photo is required as evidence of the fix.'); return; }
    setBusy(true);
    try {
      // v0.15.0 MVP - upload the photo as a base64 data URL stored on
      // DefectPhotos.Url. This matches how DropPin already stores plan
      // images. Future v0.5.x can swap to an Azure Blob upload for
      // big-file efficiency, but for sub-5MB phone photos this is
      // fine and avoids new infra.
      // v0.103.0 - downscaled copies of the evidence photo, built here so client
      // rectifications carry the same variants a staff capture does.
      const variants = await makeImageVariants(photoDataUrl);
      const r = await fetch('/api/public-rectify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          defectId: defect.id,
          comment: comment.trim(),
          photoUrl: photoDataUrl,
          photoThumbnailUrl: variants.thumbnailUrl,
          photoDisplayUrl: variants.displayUrl,
          authorName: authorName.trim() || null,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${r.status}`);
      }
      const json = await r.json();
      onSuccess(json);
    } catch (e) {
      const msg = e.message || String(e);
      setErr(msg);
      onError?.(msg);
    } finally {
      setBusy(false);
    }
  }

  // Esc to close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  return (
    <div
      onClick={() => { if (!busy) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1800, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.surface, width: 520, maxWidth: '100%',
          maxHeight: '90vh', overflowY: 'auto',
          borderRadius: 12, padding: 22,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.brand, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
          Mark rectified · #{defect.defectNumber}
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>{defect.description || '(no description)'}</h2>
        {defect.locationLabel && (
          <div style={{ fontSize: 12, color: theme.textLight, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={11} />{defect.locationLabel}</div>
        )}

        {!shareRecipient && (
          <div style={{ marginBottom: 12 }}>
            <Label>Your name <span style={{ color: theme.textLight, fontWeight: 400 }}>(optional)</span></Label>
            <input
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder="e.g. Chris Smith - Acme Tiles"
              style={fieldStyle}
              maxLength={120}
            />
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <Label>Photo of the fix *</Label>
          {photoDataUrl ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img src={photoDataUrl} alt="" style={{ display: 'block', maxWidth: '100%', maxHeight: 240, borderRadius: 6, border: `1px solid ${theme.border}` }} />
              <button
                type="button"
                onClick={clearPhoto}
                title="Pick a different photo"
                style={{
                  position: 'absolute', top: 4, right: 4,
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'rgba(15,23,42,0.85)', color: 'white',
                  border: 'none', cursor: 'pointer', fontSize: 14,
                }}
              >×</button>
            </div>
          ) : (
            <label style={{
              display: 'block',
              border: `2px dashed ${theme.border}`,
              borderRadius: 8, padding: '20px 12px',
              textAlign: 'center', fontSize: 13, color: theme.textLight,
              cursor: 'pointer',
            }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>📷</div>
              <div style={{ fontWeight: 600, color: theme.text }}>Take a photo or pick from gallery</div>
              <div style={{ fontSize: 11, marginTop: 4 }}>JPG / PNG / HEIC. Max ~5MB.</div>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onPickFile}
                style={{ display: 'none' }}
              />
            </label>
          )}
        </div>

        <div style={{ marginBottom: 12 }}>
          <Label>Comment * <span style={{ color: theme.textLight, fontWeight: 400 }}>(what did you do?)</span></Label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="e.g. Replaced cracked tile and regrouted. Cleaned the area."
            rows={4}
            maxLength={2000}
            style={{ ...fieldStyle, fontFamily: 'inherit', resize: 'vertical', minHeight: 80 }}
          />
          <div style={{ fontSize: 10, color: theme.textLight, textAlign: 'right', marginTop: 2 }}>
            {comment.length} / 2000
          </div>
        </div>

        {err && (
          <div style={{
            padding: '8px 10px', background: theme.bg, border: '1px solid ' + theme.danger,
            color: theme.danger, borderRadius: 6, fontSize: 12, marginBottom: 12,
          }}>{err}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              padding: '8px 14px', borderRadius: 6,
              background: theme.surface, color: theme.text,
              border: `1px solid ${theme.border}`,
              fontSize: 13, cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.5 : 1,
            }}
          >Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !comment.trim() || !photoDataUrl}
            style={{
              padding: '8px 16px', borderRadius: 6,
              background: theme.brand, color: 'var(--brand-on, #fff)',
              border: 'none', fontWeight: 600, fontSize: 13,
              cursor: (busy || !comment.trim() || !photoDataUrl) ? 'default' : 'pointer',
              opacity: (busy || !comment.trim() || !photoDataUrl) ? 0.5 : 1,
            }}
          >{busy ? 'Submitting…' : '✓ Mark rectified'}</button>
        </div>
      </div>
    </div>
  );
}

// v0.16.0 - Owner sign-off modal. Lighter than RectifyModal because:
//   - Comment + photo are OPTIONAL (sometimes the owner just wants
//     to wave through what the supervisor already evidenced).
//   - There's a "see the rectifier's evidence" recap at the top so
//     the owner can review before signing off.
function CompleteModal({ defect, token, shareRecipient, onClose, onSuccess, onError }) {
  const [photoFile, setPhotoFile] = useState(null);
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [comment, setComment] = useState('');
  // v0.281.0 - a link sent to a person signs as that person; only a link
  // sent to nobody asks for a name.
  const [authorName, setAuthorName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function onPickFile(e) {
    const picked = e.target.files?.[0];
    if (!picked) return;
    setErr(null);
    // 2026-09-15 — an iPhone photo (HEIC) is converted to a JPEG, at the stored
    // size, before it is read (utils/heicPhotos): no other browser can show a HEIC.
    let f;
    try { f = await toUploadableImage(picked); } catch (convErr) { setErr(convErr?.message || 'Could not read that photo file'); return; }
    setPhotoFile(f);
    const reader = new FileReader();
    reader.onload = () => setPhotoDataUrl(String(reader.result));
    reader.onerror = () => setErr('Could not read that photo file');
    reader.readAsDataURL(f);
  }
  function clearPhoto() { setPhotoFile(null); setPhotoDataUrl(null); }

  async function submit() {
    setErr(null);
    setBusy(true);
    try {
      // v0.103.0 - downscaled copies of the evidence photo, built here so client
      // rectifications carry the same variants a staff capture does.
      const variants = await makeImageVariants(photoDataUrl);
      const r = await fetch('/api/public-complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          defectId: defect.id,
          comment: comment.trim() || null,
          photoUrl: photoDataUrl,
          photoThumbnailUrl: variants.thumbnailUrl,
          photoDisplayUrl: variants.displayUrl,
          authorName: authorName.trim() || null,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${r.status}`);
      }
      const json = await r.json();
      onSuccess(json);
    } catch (e) {
      const msg = e.message || String(e);
      setErr(msg);
      onError?.(msg);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  // Pull the most recent rectifier comment + photo so the owner can
  // review before signing off.
  const rectifierComments = (defect.comments || []).filter(c => c.authorRole === 'rectifier');
  const rectifierComment = rectifierComments[rectifierComments.length - 1];
  const recentPhoto = (defect.photos || [])[defect.photos?.length - 1];

  return (
    <div
      onClick={() => { if (!busy) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1800, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.surface, width: 560, maxWidth: '100%',
          maxHeight: '90vh', overflowY: 'auto',
          borderRadius: 12, padding: 22,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.success, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
          Confirm complete · #{defect.defectNumber}
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>{defect.description || '(no description)'}</h2>
        {defect.locationLabel && (
          <div style={{ fontSize: 12, color: theme.textLight, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={11} />{defect.locationLabel}</div>
        )}

        {/* Rectification evidence recap */}
        {(rectifierComment || recentPhoto) && (
          <div style={{
            padding: 12, marginBottom: 14,
            background: theme.bg, border: '1px solid ' + theme.border,
            borderRadius: 8,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.brand, marginBottom: 6 }}>
              Rectifier evidence
            </div>
            {recentPhoto && (
              <img
                src={recentPhoto.thumbnailUrl || recentPhoto.url}
                alt=""
                style={{ display: 'block', maxWidth: '100%', maxHeight: 160, borderRadius: 4, marginBottom: 8 }}
              />
            )}
            {rectifierComment && (
              <div style={{ fontSize: 12, color: theme.text, whiteSpace: 'pre-wrap' }}>
                {rectifierComment.body}
                <div style={{ fontSize: 10, color: theme.textLight, marginTop: 4 }}>
                  — {rectifierComment.authorName || 'rectifier'}, {fmtDate(rectifierComment.createdAt)}
                </div>
              </div>
            )}
          </div>
        )}

        {!shareRecipient && (
          <div style={{ marginBottom: 12 }}>
            <Label>Your name <span style={{ color: theme.textLight, fontWeight: 400 }}>(optional)</span></Label>
            <input
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder="e.g. Sam Owner"
              style={fieldStyle}
              maxLength={120}
            />
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <Label>Confirmation photo <span style={{ color: theme.textLight, fontWeight: 400 }}>(optional)</span></Label>
          {photoDataUrl ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img src={photoDataUrl} alt="" style={{ display: 'block', maxWidth: '100%', maxHeight: 200, borderRadius: 6, border: `1px solid ${theme.border}` }} />
              <button
                type="button"
                onClick={clearPhoto}
                title="Remove photo"
                style={{
                  position: 'absolute', top: 4, right: 4,
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'rgba(15,23,42,0.85)', color: 'white',
                  border: 'none', cursor: 'pointer', fontSize: 14,
                }}
              >×</button>
            </div>
          ) : (
            <label style={{
              display: 'block',
              border: `2px dashed ${theme.border}`,
              borderRadius: 8, padding: '12px',
              textAlign: 'center', fontSize: 12, color: theme.textLight,
              cursor: 'pointer',
            }}>
              <span style={{ fontSize: 20, marginRight: 6 }}>📷</span>
              Take a photo or upload
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onPickFile}
                style={{ display: 'none' }}
              />
            </label>
          )}
        </div>

        <div style={{ marginBottom: 12 }}>
          <Label>Sign-off note <span style={{ color: theme.textLight, fontWeight: 400 }}>(optional)</span></Label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="e.g. Confirmed on site Wednesday. Looks good."
            rows={3}
            maxLength={2000}
            style={{ ...fieldStyle, fontFamily: 'inherit', resize: 'vertical', minHeight: 60 }}
          />
          <div style={{ fontSize: 10, color: theme.textLight, textAlign: 'right', marginTop: 2 }}>
            {comment.length} / 2000
          </div>
        </div>

        {err && (
          <div style={{
            padding: '8px 10px', background: theme.bg, border: '1px solid ' + theme.danger,
            color: theme.danger, borderRadius: 6, fontSize: 12, marginBottom: 12,
          }}>{err}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              padding: '8px 14px', borderRadius: 6,
              background: theme.surface, color: theme.text,
              border: `1px solid ${theme.border}`,
              fontSize: 13, cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.5 : 1,
            }}
          >Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            style={{
              padding: '8px 16px', borderRadius: 6,
              background: theme.success, color: 'white',
              border: 'none', fontWeight: 600, fontSize: 13,
              cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.5 : 1,
            }}
          >{busy ? 'Signing off…' : '✓✓ Confirm complete'}</button>
        </div>
      </div>
    </div>
  );
}

// Shared field styles for the rectify modal (inline so the file stays
// self-contained for the public route).
const fieldStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid ' + theme.border,
  borderRadius: 6,
  fontSize: 14,
  boxSizing: 'border-box',
};
function Label({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 }}>
      {children}
    </div>
  );
}

// v0.36.1 — Lightbox now lives at ../components/PhotoLightbox.jsx
//           (was inlined here as a local function in v0.13.0). PlanScreen's
//           defect-edit modal consumes the same component so the two
//           viewers don't drift apart.

